import { getToken, clearToken } from "@/lib/auth";
import { normalizeUploadResponse, type UploadResult } from "@/lib/upload-errors";

const API_BASE = (import.meta.env.VITE_API_URL ?? "") + "/api";

const headers = () => {
  const token = getToken();
  return { Authorization: token ? `Bearer ${token}` : "" };
};

type ApiErrorHandler = (message: string, status: number) => void;
let _onApiError: ApiErrorHandler | null = null;

export function setApiErrorHandler(fn: ApiErrorHandler) {
  _onApiError = fn;
}

export class ApiError extends Error {
  // `code` is the backend's machine-readable error key (structured detail);
  // branch on it, not on the message.
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
    this.name = "ApiError";
  }
}

function _extractHttpError(body: Record<string, unknown>, fallback: string): string {
  // FastAPI HTTPException  → { detail: string }
  // Pipeline 500 response → { detail: string } or { error: string }
  if (typeof body.detail === "string") return body.detail;
  if (body.detail && typeof body.detail === "object") {
    const d = body.detail as Record<string, unknown>;
    if (d.error === "missing_required_columns" && Array.isArray(d.missing)) {
      return `Required columns not found: ${(d.missing as string[]).join(", ")}`;
    }
    if (d.error === "book_data_exists") {
      return "This portfolio already has data loaded. Delete the previous run before loading it again.";
    }
    if (d.error === "destructive_mapping_write" && Array.isArray(d.losses)) {
      return `This change would discard existing mapping configuration: ${(d.losses as string[]).join("; ")}. Reload the page so you are editing the current config, or contact an admin to remove it deliberately.`;
    }
    if (d.error === "missing_mapped_fields" && Array.isArray(d.missing)) {
      return `The book's mapping does not provide fields this facility's rules need: ${(d.missing as string[]).join(", ")}. Add them in the book's Mapping tab before activating.`;
    }
    if (typeof d.message === "string") return d.message;
    if (typeof d.msg === "string") return d.msg;
  }
  if (typeof body.error === "string") return body.error;
  return fallback;
}


import { cacheGet, cacheSet, cacheInvalidate, getInFlight, setInFlight, deleteInFlight, TTL } from "./apiCache";
import type {
  FacilityInfo, FacilityListItem, Metrics, Analytics, WaterfallRunRecord, WaterfallRunsResponse,
  CovenantObservationsResponse, IncidentRecord, IncidentStatus, IncidentDetail,
  IncidentsSummary, EligibilityViews, AcquisitionCohortsResponse, FacilityGlanceResponse, LineMovementsResponse, NoteLedger,
  DpdStratificationResponse, AssetTestResponse, PeriodCostResponse,
} from "@/pages/facility/facility.types";

import type { BorrowerProfileInput, BorrowerProfileResponse } from "@/types/borrowerProfile";
import type { BorrowerFinancialsResponse, FinancialDocumentRecord } from "@/types/borrowerFinancials";

// Re-exported for IncidentsInbox, IncidentDetailModal, useGlobalStore.
export type { IncidentRecord, IncidentStatus, IncidentDetail, IncidentsSummary } from "@/pages/facility/facility.types";

/** A borrower row as `GET /borrowers` returns it. `book` is null until one is
 *  onboarded; every figure inside it comes from that book's latest snapshot. */
export type BorrowerListEntry = {
  id: string;
  legal_name: string;
  country: string | null;
  jurisdiction: string | null;
  currency: string;
  kyc_status: "PENDING" | "APPROVED" | "REJECTED";
  facility_count: number;
  /** Contracts (Deals), not facilities. */
  deal_count?: number;
  has_active_book: boolean;
  /** Whether a presentation card exists; the document is not in the list. */
  has_profile?: boolean;
  book: {
    id: string;
    currency: string;
    asset_class: string | null;
    asset_class_label: string | null;
    snapshot_date: string | null;
    outstanding: number | null;
    disbursed: number | null;
    write_off_rate: number | null;
    par_buckets: Record<string, number> | null;
    /** Live loans that reported no DPD: outside the base of `par_buckets`. */
    missing_dpd?: { loans: number; outstanding: number } | null;
    loan_count: number | null;
    last_run: { status: string; pipeline_type: string | null; started_at: string | null } | null;
  } | null;
};

/* The Deal: the contract 1-N facilities live under.
 *
 * Transcribed from `servicing_engine/api/deals.py`.
 * `tests/test_wire_shape_baseline.py` pins these key sets against the API, so a
 * renamed key fails there instead of rendering `undefined`.
 */

/** Category of a trust movement: the first three on credits, `bank_fee_tax`
 *  on debits. */
export type TrustCategory = "gateway_sweep" | "originator_sweep" | "non_collection" | "bank_fee_tax";

/** One rule of `deal_config.reconciliation.classification`. `pattern` is a
 *  case-insensitive regular expression searched in `field`. A gateway sweep
 *  names the gateway's transit account; `subtype` is for non_collection. */
export type TrustClassificationRule = {
  category: TrustCategory;
  field: "reference" | "description" | "counterparty";
  pattern: string;
  source_account_id?: string | null;
  subtype?: string | null;
};

/** `deal_config`: the contract's term sheet. Known blocks are validated
 *  server-side (`schemas/deal_config.py`); anything else passes through.
 *  `GET /deals/{id}` serves it as stored and as effective (code defaults
 *  resolved). */
export type DealConfig = {
  /** When the contract pays: its only calendar. `determination` is the legacy
   *  key, folded into `payment` on read; stored configs may still carry it. */
  payment?: { frequency: "monthly" | "quarterly"; day: number } | null;
  determination?: { frequency: "monthly" | "quarterly"; day: number } | null;
  /** Tolerances of the Deal's payment matching. */
  reconciliation?: {
    amount_tolerance_abs: number;
    amount_propose_pct: number;
    date_tolerance_days: number;
    /** Shortest normalized loan ID proposed when a reference only contains
     *  it; null proposes no partial ID. */
    partial_id_min_length: number | null;
    single_candidate_days: number;
    /** Business days a reported payment without a collection, or a
     *  collection without a reported payment, stays pending. */
    aging_days?: number;
    /** Business days a gateway has, from a settlement, to land it in the trust. */
    sweep_days?: number;
    /** Shortest common part of a cut settlement reference; null demands equal ones. */
    sweep_ref_min_length?: number | null;
    /** ISO 3166-1 alpha-2: whose public holidays the business days skip. */
    country?: string | null;
    /** IANA time zone where "today" is read. */
    timezone?: string | null;
    /** Rules that classify the trust account's movements; first match wins. */
    classification?: TrustClassificationRule[];
  };
  covenant_defaults?: { cure_days: number | null; on_breach: string };
  credits?: { allocation: "none" | "pro_rata_obligation" | "fixed_shares"; shares?: Record<string, number> };
  /** The Pricing Annex the contract signed. No default: while absent, the
   *  platform fee is pending, not zero. */
  pricing_annex?: string;
  /** The contract's Priority of Payments: the ladder the Payment Report fills. */
  waterfall?: { steps: DealLadderStep[] };
  [key: string]: unknown;
};

/** One rung of `deal_config.waterfall`, as stored. Exactly one source: the
 *  facility categories it `collects`, a Deal-level `step`, or neither (a
 *  declared line named by `label`; without `amount` it is pending, never zero). */
export type DealLadderStep = {
  priority: number;
  key?: string | null;
  label?: string | null;
  recipient?: string | null;
  collects?: string[] | null;
  step?: string | null;
  amount?: number | null;
};

/** One Pricing Annex as `GET /deals/{id}/config-options` offers it to this
 *  contract. `available: false` comes with the reason the write would give. */
export type DealPricingAnnexOption = {
  version: string;
  description: string;
  currency: string;
  fee_day_count: string;
  /** The contract an annex was negotiated for, when it was for one. */
  exclusive_to: string | null;
  available: boolean;
  refusal: string | null;
};

/** What the contract's config may say (annexes, rung vocabulary), so the editor
 *  offers only what the PUT accepts. */
export type DealConfigOptions = {
  deal_id: string;
  deal_reference: string;
  pricing_annex: string | null;
  pricing_annexes: DealPricingAnnexOption[];
  ladder: {
    /** The facility categories a rung may sum. */
    collects: string[];
    /** The kinds charged on the contract as a whole. */
    steps: string[];
  };
};

/** A row of `GET /deals`: no config, no facilities, no sums. `status` is
 *  derived from the live facilities («≥1 ACTIVE ⇒ ACTIVE»), not a column. */
export type DealListItem = {
  id: string;
  deal_reference: string;
  deal_name: string;
  book_id: string;
  borrower_id: string;
  borrower: { id: string; legal_name: string; country: string | null; kyc_status: string } | null;
  currency: string;
  status: "ACTIVE" | "DRAFT";
  facility_count: number;
  /** The contract's life from its launched facilities: earliest disbursement,
   *  latest maturity. Null when no facility declares the date. */
  disbursement_date: string | null;
  maturity_date: string | null;
  /** Null under an anonymous grant: an email domain is a name by another route. */
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** True when this payload was served under an anonymous grant. */
  anonymized?: boolean;
};

/** A facility as the Deal lists it: lighter than `FacilityInfo`, enough to name
 *  it, size it and link to its cockpit.
 *
 *  `borrowing_base_cockpit` is the latest `daily_metrics` figure, the one the
 *  cockpit shows. The Deal does not sum it: facilities can be sealed in
 *  different currencies. */
export type DealFacilityStub = {
  id: string;
  facility_reference: string;
  facility_name: string;
  deal_id: string;
  book_id: string;
  status: string;
  currency: string;
  facility_type: string;
  max_facility_size: number | null;
  note_balance: number | null;
  borrowing_base_cockpit: number | null;
  borrowing_base_cockpit_date: string | null;
  /** End of the reinvestment period: disbursements stop and the paper
   *  amortises. Null where the contract declares none (a term, not a gap). */
  reinvestment_end_date?: string | null;
  updated_at: string | null;
  /** The facility's life. Null where it declares none (activation requires both). */
  disbursement_date?: string | null;
  maturity_date?: string | null;
};

/** `GET /deals/{id}`: the list row plus the config (twice) and the live
 *  facilities. Both configs come back null under an anonymous grant. */
export type DealInfo = DealListItem & {
  deal_config: DealConfig | null;
  deal_config_effective: DealConfig | null;
  facilities: DealFacilityStub[];
};

/** One column of a rung, or of the whole report.
 *
 *  `amount` sums what is known; `complete` says whether anything is missing, so
 *  a partial sum is never shown as complete. `missing` names which facility
 *  could not price which category and why. */
export type LadderColumn = {
  amount: number | null;
  complete: boolean;
  missing: {
    facility_id: string | null;
    facility_reference: string | null;
    category: string | null;
    reason: string | null;
  }[];
};

/** One rung of the contract's Priority of Payments.
 *
 *  `source`: summed from the facilities, charged on the contract, declared in
 *  its config, or unknown. `RESIDUAL` is the excess line and carries no amount:
 *  the report states what is owed, never the cash that came in. */
export type LadderRung = {
  priority: number;
  key: string | null;
  label: string | null;
  recipient: string | null;
  source: "FACILITIES" | "DEAL" | "DECLARED" | "UNKNOWN" | "RESIDUAL" | "INCLUDED";
  accrued: LadderColumn;
  projected: LadderColumn;
  /** One line per contributing facility, plus, on the platform fee rung, one
   *  with no facility: the Series' monthly minimum. A line with both a figure
   *  and a gap is partly priced; the figure is the known part. */
  breakdown: {
    facility_id: string | null;
    facility_reference: string | null;
    /** The run this facility's figure was read from. Null on the contract's
     *  own line (the Series' monthly minimum). */
    metric_date?: string | null;
    category: string;
    label?: string | null;
    accrued: number | null;
    projected: number | null;
    accrued_gap: string | null;
    projected_gap: string | null;
  }[];
  note: string | null;
};

/** One column of the Series' monthly minimum: the top-up it adds to the
 *  platform fee rung. `amount` is null when a facility's fees are unknown and
 *  the known ones do not reach the floor. */
export type SeriesMinimumColumn = {
  /** In the annex's currency (`currency`, USD). */
  monthly_minimum: number | null;
  draw_fee_monthly_minimum: number | null;
  currency?: string;
  /** Contract's currency per unit of the annex's, at the report's rate. */
  annex_rate?: number | null;
  /** Share of the period elapsed: the accrued floor is prorated by it. */
  fraction?: number | null;
  /** The floors as charged in this column: in the contract's currency, prorated. */
  monthly_minimum_charged?: number | null;
  draw_fee_monthly_minimum_charged?: number | null;
  lines: number;
  lines_with_balance_out: number;
  series_fees: number;
  series_fees_complete: boolean;
  amount: number | null;
  applies: boolean;
  gap: string | null;
};

/** Which run one facility's figures on the Payment Report come from. */
export type LadderMeasurement = {
  facility_id: string;
  facility_reference: string;
  metric_date: string | null;
  figures_as_of: string | null;
  exact: boolean;
  pipeline_status: string | null;
};

/** `GET /deals/{id}/period-cost`: the Payment Report.
 *
 *  Every amount is in the contract's `currency`, converted at the `as_of` rate
 *  for both columns. `projected_to` is null when the contract declares no
 *  payment calendar, and `projection_gap` says so. */
export type DealPeriodLadder = {
  /** Covenant consequences engaged on the date, per facility. Signalled only:
   *  they do not change the order of payment. */
  active_consequences?: { facility_id: string; facility_reference: string; consequences: string[] }[];
  deal_id: string;
  deal_reference: string;
  currency: string;
  /** The date the figures were measured on: the runs each facility recorded,
   *  read with the facility's own rule. */
  as_of: string;
  /** The date that was asked. */
  requested_as_of?: string;
  /** Whether every figure comes from that date's own run; otherwise the last
   *  run before it is used. */
  exact?: boolean;
  /** False when facilities were measured on different days: rungs and totals
   *  are then incomplete; `period_alignment_gap` says which was measured when. */
  period_aligned?: boolean;
  period_alignment_gap?: string | null;
  measurements?: LadderMeasurement[];
  period_start: string | null;
  projected_to: string | null;
  projection_gap: string | null;
  facility_count: number;
  excluded_facilities: { facility_id: string; facility_reference: string; reason: string }[];
  /** The Series' monthly minimum, computed once over every facility. Null when
   *  the annex has no monthly floor. */
  series_minimum?: {
    annex: string;
    monthly_minimum: number | null;
    draw_fee_monthly_minimum: number | null;
    accrued: SeriesMinimumColumn | null;
    projected: SeriesMinimumColumn | null;
    note: string;
  } | null;
  rungs: LadderRung[];
  accrued: { total: number | null; complete: boolean; unpriced: (string | null)[] };
  projected: { total: number | null; complete: boolean; unpriced: (string | null)[] };
  anonymized?: boolean;
};

/** An entry of `GET /deals/{id}/audit`: contract and facility rows, told apart
 *  by `scope`. Facility fields are null on a contract-scoped entry. */
export type DealAuditEntry = {
  id: string;
  scope: "deal" | "facility";
  facility_id: string | null;
  facility_reference: string | null;
  config_type: string;
  action: string | null;
  previous_value: unknown;
  new_value: unknown;
  changed_by: string | null;
  created_at: string | null;
};

/** One live grant that reaches a Deal: written against it (`scope: "deal"`)
 *  or the book's `deals_view` wildcard (`scope: "book"`). */
export type DealAccessGrant = {
  id: string;
  email: string;
  permission: string;
  scope: "deal" | "book";
  deal_id: string | null;
  anonymized: boolean;
  granted_by: string | null;
  granted_at: string | null;
};

/** `GET /deals/{id}/access`: who reads this contract, and therefore who will
 *  read any facility added to it without a new grant. Facility creation shows
 *  it before confirming (visibility, not a block). */
export type DealAccess = { deal_id: string; count: number; grants: DealAccessGrant[] };

/* The Deal's two read-models: the covenant rollup and the aggregate.
 *
 * Computed on read, never stored. Transcribed from `services/covenant_rollup.py`
 * and `services/deal_aggregate.py`, pinned in `tests/test_wire_shape_baseline.py`.
 * Every figure travels with the denominator that qualifies it; read both.
 */

/** One facility inside a metric group: measurement, threshold and margin. They
 *  live at this level because they belong to that facility's contract only. */
export type DealCovenantFacility = {
  facility_id: string;
  facility_reference: string;
  facility_name: string;
  /** Every rule this facility puts on the metric, in the contract's own wording.
   *  Empty under an anonymous grant. */
  rule_names: string[];
  /** The rule whose observation governs (the worst). Null when defined but not
   *  measured, and under an anonymous grant. */
  covenant_key: string | null;
  rules_observed: number;
  status: DealCovenantStatus;
  measured_value: number | null;
  threshold: number | null;
  headroom: number | null;
  /** Why `threshold` is that one, on a covenant with stepped thresholds
   *  («1.40x because bb_exit_rate is 10.14% (> 8%) since …»); null otherwise. */
  threshold_reason: string | null;
  /** What the contract declares happens when the governing rule breaks
   *  (`cash_trap`, `stop_revolving`, `early_amortization`, `notify`), whatever
   *  the verdict. A list when it chains several. Null where none is declared
   *  (a real case, not a gap).
   *
   *  Reported, never applied: consequences the engine applies belong to the
   *  facility's own cascade. */
  on_breach: string | string[] | null;
  /** Days to cure before the consequence is due. Null is not zero: a rule with
   *  no cure period bites on the determination date. */
  cure_days: number | null;
  severity: string | null;
  /** Every rule this facility wrote on the metric, not only the governing one
   *  (e.g. a cash trap at 1.20x and stop-revolving at 1.12x are two events).
   *  `name` is null under an anonymous grant; the terms stay. */
  rules: DealCovenantRule[];
  /** First determination of the breach running now (an earlier, cured breach
   *  does not count), and how many determinations it has lasted. Null
   *  throughout where nothing is breaking; a zero would read as a breach. */
  breached_since: string | null;
  /** Days from `breached_since` to the report date, not to the last
   *  determination. */
  days_in_breach: number | null;
  breach_determinations: number | null;
  /** Null, not false, where the rule declares no cure period («no cure» is not
   *  «cured in time»). */
  cure_expired: boolean | null;
  determination_date: string | null;
  /** Worst verdict of the runs after the latest official one (surveillance,
   *  between payment dates); null when there is none. Never the declared status. */
  observed_status?: DealCovenantStatus | null;
};

/** The latest run after a rule's official determination: surveillance, never
 *  a verdict of the contract. */
export type DealCovenantObserved = {
  status: DealCovenantStatus;
  measured_value: number | null;
  threshold: number | null;
  determination_date: string;
};

/** One rule of a facility's covenant config, with what was last measured
 *  against it. The rule is the unit: a facility with a 1.20x and a 1.12x floor
 *  has one figure and two verdicts. */
export type DealCovenantRule = {
  /** The contract's own wording. Null under an anonymous grant. */
  name: string | null;
  metric: string | null;
  /** Comparison operator: what makes 0.06 a cap and 1.20 a floor. */
  op: string | null;
  /** What the config says today. */
  threshold: number | null;
  /** Warning level before the breach, where declared. Null is not zero: most
   *  rules have none. */
  warning: number | null;
  /** One consequence, or the chain of them a breach triggers. */
  on_breach: string | string[] | null;
  cure_days: number | null;
  severity: string | null;
  status: DealCovenantStatus;
  measured_value: number | null;
  /** What the observation was judged against, sealed on the row. Differs from
   *  `threshold` exactly when the contract was amended since; show both. */
  measured_threshold: number | null;
  headroom: number | null;
  determination_date: string | null;
  breached_since: string | null;
  days_in_breach: number | null;
  breach_determinations: number | null;
  cure_expired: boolean | null;
  /** The latest run after this determination, if any. */
  observed?: DealCovenantObserved | null;
  /** The money it takes to put the ratio back inside this line. Null where the
   *  rule is not a ratio the engine can decompose. */
  cure: DealCovenantCure | null;
  /** Incidents opened when this rule broke, newest first: where in the
   *  procedure the breach is. */
  incidents: DealCovenantIncident[];
  /** How the figure was arrived at, where the rest of the screen does not show
   *  it (e.g. the exit rate, a flow over a month). Null unless the recomputed
   *  workings reproduce the sealed observation. */
  workings: DealCovenantWorkings | null;
};

export type DealCovenantWorkings = {
  kind: "flow";
  /** The facility's currency (both sides are read off its run). */
  currency: string;
  numerator: number | null;
  numerator_label: string;
  denominator: number | null;
  denominator_label: string;
  window_from: string | null;
  window_to: string | null;
  /** Days of the window that carried a base; may be fewer than the window. */
  days_measured: number | null;
  partial_denominator: boolean;
};

/** One breach incident. `status` walks OPEN → ESCALATED → CURED/RESOLVED.
 *  `period` is the determination it was raised on, not the day the row was
 *  written (a backdated run dates it when the contract broke). */
export type DealCovenantIncident = {
  id: string;
  status: string;
  severity: string;
  period: string | null;
  opened_at: string | null;
  cure_deadline: string | null;
  escalated_at: string | null;
  escalation_reason: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_note: string | null;
  message: string;
};

/** The two ways back inside a line, in the facility's currency: move the
 *  numerator or the denominator (different actions, e.g. posting collateral vs
 *  repaying the line).
 *
 *  Moves are signed, read against the operator: `t·D − N` and `N/t − D`. On a
 *  floor a positive numerator move is what is missing; on a cap, the room left. */
export type DealCovenantCure = {
  currency: string;
  numerator: number | null;
  denominator: number | null;
  /** Each side of the ratio in words, e.g. «eligible collateral and contributed
   *  cash» over «what the facility owes». */
  numerator_label: string;
  denominator_label: string;
  numerator_move: number | null;
  denominator_move: number | null;
  /** Why there are no figures: the run did not seal one side of the ratio. */
  reason: string | null;
};

/** The borrowing base against what is drawn on it. Below 1.00 the contract has
 *  drawn more than its base permits; `excess_drawn` says by how much (null at or
 *  above the line). Carries no verdict: whether an over-advance matters is a
 *  term. */
export type DealBbCoverage = {
  value: number | null;
  borrowing_base: number | null;
  /** Owed on each facility's run date, as `DealContractOc.drawn`. */
  drawn: number | null;
  drawn_dates: string[];
  excess_drawn: number | null;
  currency: string;
  facility_count: number;
  /** The same ratio per facility: the contract's is a ratio of sums and can sit
   *  above the line with a facility under it. */
  facilities: {
    facility_id: string;
    facility_reference: string;
    borrowing_base: number | null;
    drawn: number | null;
    value: number | null;
    excess_drawn: number | null;
  }[];
  unmeasured_facilities: { facility_id: string; facility_reference: string }[];
  excluded_facilities: { facility_id: string; facility_reference: string }[];
  reason: string | null;
};

/** `GET /facilities/{id}/analytics/{section}`: one calculator's output.
 *  `result` is null where the section was never computed for that snapshot;
 *  render «not reported», never zero. */
export type FacilityAnalyticsSection<T = unknown> = {
  facility_id: string;
  snapshot_date: string | null;
  section: string;
  result: T | null;
  calculated_at: string | null;
};

/** `GET /deals/{id}/report-dates`: the dates this contract was actually
 *  reported on. Other surfaces answer for any `as_of`, carrying the previous
 *  run forward. Newest first, completed runs only (a running one is partial). */
export type DealReportDates = {
  deal_id: string;
  /** True where the contract has more dates than the list carries. */
  truncated: boolean;
  total: number;
  dates: DealReportDate[];
};

export type DealReportDate = {
  /** The run's snapshot date, not the day it was launched. */
  date: string;
  runs: number;
  /** Whether the contract pays on it (a determination; other dates are
   *  indicative). Null, never false, where no readable calendar is declared. */
  payment_date: boolean | null;
};

/** `GET /deals/{id}/payment-schedule`: the contract's payment dates, what each
 *  cost and what was recorded against it. `dates` is empty with a `reason`
 *  where the contract declares no calendar; dates are never inferred from
 *  movements. */
export type DealPaymentSchedule = {
  deal_id: string;
  as_of: string;
  currency: string;
  frequency: string | null;
  day: number | null;
  /** Why the schedule is incomplete: no calendar, or earlier dates left off a
   *  contract too long to price in full. Null when complete. */
  reason: string | null;
  /** What the reserve has to hold today (also on every row, per date). Null
   *  where the contract declares no reserve (a term, not a gap). */
  reserve: DealPaymentReserve | null;
  dates: DealPaymentDate[];
};

export type DealPaymentRung = {
  priority: number;
  label: string | null;
  recipient: string | null;
  /** `FACILITIES` asked of each facility · `DEAL` charged once on the contract
   *  · `UNKNOWN` declared but unpriced · `RESIDUAL` what is left over (never an
   *  amount). */
  source: string | null;
  amount: number | null;
  complete: boolean;
  /** The cost categories the rung collects (`LINE_INTEREST`, `PLATFORM_FEE`…);
   *  empty on a rung charged on the contract or not priced. */
  categories?: string[];
};

export type DealPaymentReserve = {
  months: number | null;
  required: number | null;
  currency: string;
  gap: string | null;
};

export type DealPaymentDate = {
  /** A payment date strictly after disbursement (nothing is due on that day). */
  date: string;
  /** Cost of the period closing on this date. Null past the next date: the
   *  ladder only prices a period that has started. */
  due: {
    amount: number | null;
    complete: boolean;
    unpriced: string[];
    /** True wherever the figure is not a closed period's. */
    projected: boolean;
    /** `ACCRUED` what a closed period cost · `PROJECTED` what the running one
     *  will (Payment Report column) · `HELD_FLAT` what a later date would cost
     *  at today's balances; moves when a draw or amortisation is recorded. */
    basis: "ACCRUED" | "PROJECTED" | "HELD_FLAT";
    /** The contract's ladder on that date. Every rung keeps its line, priced or
     *  not: dropping unpriced rungs would hide what is missing from the total. */
    rungs: DealPaymentRung[];
  } | null;
  /** Principal returned on this date. `amortised` is only what the contract
   *  declares (notes' calendar, line repayments); `at_maturity` is whatever is
   *  still outstanding when a facility matures (the whole balance if nothing
   *  amortises). Null where the period returns nothing (a term, not a gap). */
  principal: {
    amount: number;
    amortised: number | null;
    at_maturity: number | null;
    source: "SCHEDULE" | "MATURITY" | "SCHEDULE_AND_MATURITY";
  } | null;
  /** Ladder plus principal. `complete` carries the ladder's, so a total short
   *  of a rung is marked short. */
  total: { amount: number; complete: boolean } | null;
  /** What the ledger records against the date (amortisations, repayments and
   *  their interest), past movements only. Null where nothing is recorded, which
   *  is not zero: the payment may not have been captured. */
  paid: number | null;
  paid_count: number;
  /** `PAID` · `PARTIAL` · `NO_PAYMENT_RECORDED` · `NEXT` · `SCHEDULED`. Never
   *  «unpaid»: no record is not evidence of a default. */
  status: string;
  /** What the reserve had to hold that day: N months of interest on what was
   *  owed then. Null where the contract declares no reserve. */
  reserve_required: DealPaymentReserve | null;
};

/** `GET /deals/{id}/covenant-series`: what each facility measured over time
 *  (the timeline carries only verdicts). One series per facility and rule;
 *  nothing is averaged or summed across facilities. */
export type DealCovenantSeries = {
  deal_id: string;
  as_of: string;
  since: string | null;
  facility_count: number;
  metric_count: number;
  metrics: {
    metric: string;
    label: string;
    series_count: number;
    series: DealCovenantCurve[];
  }[];
  anonymized?: boolean;
};

export type DealCovenantCurve = {
  facility_id: string;
  facility_reference: string;
  facility_name: string;
  /** The rule the curve belongs to. Null under an anonymous grant. */
  covenant_key: string | null;
  point_count: number;
  points: {
    date: string;
    value: number | null;
    /** Sealed on the observation: an amended threshold moves on the date it
     *  changed, never today's value drawn backwards. */
    threshold: number | null;
    headroom: number | null;
    status: DealCovenantStatus;
  }[];
};

/** The four verdicts a facility entry or group can carry. `UNABLE_TO_EVALUATE`
 *  is a metric owed with nothing measured at `as_of`: not a zero and not on the
 *  BREACHED > WARNING > COMPLIANT ladder. */
export type DealCovenantStatus = "BREACHED" | "WARNING" | "COMPLIANT" | "UNABLE_TO_EVALUATE";

/** A metric group: verdict, counts and worst headroom, with no measure,
 *  threshold or headroom of its own (averaging two contracts' thresholds yields
 *  a number in neither). */
export type DealCovenantMetric = {
  metric: string;
  /** The catalog's description (platform vocabulary, English). */
  label: string;
  status: DealCovenantStatus;
  /** Facilities of the contract that define the metric (what is owed, not what
   *  a pipeline produced). */
  defining_count: number;
  /** Of those, how many were measured at `as_of`. */
  evaluated_count: number;
  breached_count: number;
  status_counts: Record<DealCovenantStatus, number>;
  /** The worst margin with its facility, so it is not read as the Deal's
   *  headroom. Null when nothing was measured. */
  worst_headroom: { value: number; facility_id: string; facility_reference: string } | null;
  facilities: DealCovenantFacility[];
};

/** `GET /deals/{id}/covenants`. No "Deal is breached" key by design: a verdict
 *  belongs to a metric. */
export type DealCovenants = {
  deal_id: string;
  as_of: string;
  /** Launched facilities: the denominator. DRAFTs are never counted;
   *  `non_active_count` says how many the contract holds. */
  facility_count: number;
  non_active_count: number;
  metric_count: number;
  metrics: DealCovenantMetric[];
};

/** The single quote the aggregate uses: Deal currency against book currency at
 *  the report date. `available: false` means converted figures are null, never
 *  zero; `reason` names the pair and the window. */
export type DealAggregateFx = {
  available: boolean;
  is_identity: boolean;
  /** Units of `native_currency` per 1 unit of `currency`. */
  rate: number | null;
  rate_date: string | null;
  source: string | null;
  reason: string | null;
};

/** The contract's borrowing base: an object because a facility may legitimately
 *  not have one. `status_counts` adds up to `facility_count`. */
export type DealAggregateBorrowingBase = {
  value: number | null;
  contributing_count: number;
  status_counts: Record<DealBorrowingBaseStatus, number>;
};

/** Why a facility's borrowing base is or is not in the total.
 *  `NOT_APPLICABLE`: a securitization's note is already funded, so no base (null,
 *  never zero). `NOT_SEALED`: the run sealed no native base, so its figure is in
 *  the wrong currency to add. */
export type DealBorrowingBaseStatus =
  | "INCLUDED" | "NOT_APPLICABLE" | "NOT_SEALED" | "UNABLE_TO_EVALUATE";

/** Amounts by category and counts; no ratios (their denominators belong to
 *  different contracts). Null throughout when nothing contributed: zero means
 *  "the pools are empty", not "nobody has run". */
export type DealAggregateTotals = {
  financed_outstanding: number | null;
  eligible_amount: number | null;
  /** Contributed cash each facility declares, summed (facilities are disjoint).
   *  Not the bank-account cash (`GET /deals/{id}/cash`). Null when one
   *  facility's could not be read in book currency, which would understate the
   *  OC numerator. */
  contributed_cash: number | null;
  financed_loan_count: number | null;
  loan_count_eligible: number | null;
  borrowing_base: DealAggregateBorrowingBase;
};

/** One facility's contribution. `*_native` is the book-currency figure its run
 *  captured; the unprefixed one is that figure converted once at the
 *  aggregate's rate. `currency` is the facility's own and is neither. */
export type DealAggregateFacility = {
  facility_id: string;
  facility_reference: string;
  facility_name: string;
  currency: string;
  status: "MEASURED" | "UNABLE_TO_EVALUATE";
  metric_date: string | null;
  native_sealed: boolean;
  financed_outstanding_native: number | null;
  eligible_amount_native: number | null;
  financed_outstanding: number | null;
  eligible_amount: number | null;
  contributed_cash_native: number | null;
  contributed_cash: number | null;
  /** The facility's own OC ratio from its run; unitless, never converted. Its
   *  threshold and verdict live in the covenant rollup. */
  oc_ratio: number | null;
  /** The share this facility's annex lends against eligible collateral. A term,
   *  per facility, never rolled up. Null when tranches are priced separately
   *  (a table, not a rate). */
  advance_rate: number | null;
  /** Arrears with its own status. Never render 0 unless MEASURED: an unmeasured
   *  pool is not a clean pool. */
  par_30_plus: DealFacilityArrears;
  /** The earlier band, read beside `par_30_plus`: disjoint tranches over the same
   *  divisor. Without it, a pool at 0% PAR30 with 1–30 DPD arrears reads clean. */
  par_1_30: DealFacilityArrears;
  financed_loan_count: number | null;
  loan_count_eligible: number | null;
  borrowing_base_status: DealBorrowingBaseStatus;
  borrowing_base_native: number | null;
  borrowing_base: number | null;
};

/** `GET /deals/{id}/aggregate`. `currency` is the contract's; `native_currency`
 *  is the book's, in which figures are summed before the single conversion. */
export type DealAggregate = {
  deal_id: string;
  as_of: string;
  currency: string;
  native_currency: string;
  fx: DealAggregateFx;
  /** Launched facilities: the denominator. DRAFTs are never counted;
   *  `non_active_count` says how many others the contract holds. */
  facility_count: number;
  non_active_count: number;
  contributing_count: number;
  complete: boolean;
  /** False when the contributing runs are not all of one date (the total still
   *  uses one rate, but is not one snapshot). */
  period_aligned: boolean;
  metric_dates: string[];
  totals: DealAggregateTotals;
  facilities: DealAggregateFacility[];
  /** The outer of the two nested limits. Absent on older payloads. */
  limits?: DealContractLimits;
  /** The reserve requirement. Absent on older payloads. */
  dsra?: DealDsra;
  /** The contract's own overcollateralisation. Absent on older payloads. */
  oc?: DealContractOc;
  /** The borrowing base against what is drawn. Not the OC (eligible collateral
   *  over debt): this is the collateral after each facility's advance rate.
   *  Absent on older payloads. */
  bb_coverage?: DealBbCoverage;
  /** How long the contract still has to run. Absent on older payloads. */
  remaining_term?: DealRemainingTerm;
};

/** Months from `as_of` to the last maturity among the launched facilities.
 *  Signed: a matured contract reads negative.
 *
 *  Null (with `maturity_date` withheld and the facilities named) whenever one
 *  facility declares no maturity: the dated ones alone would understate the
 *  term. */
export type DealRemainingTerm = {
  months: number | null;
  days: number | null;
  maturity_date: string | null;
  facility_count: number;
  undated_facilities: { facility_id: string; facility_reference: string }[];
  reason: string | null;
};

/** One facility's days-past-due share and the pool it was taken over. Not
 *  aggregated into contract totals: each pool passed a different eligibility
 *  cut.
 *
 *  `status`: whether the share could be measured; `amount_status`: whether the
 *  amount could be stated in book currency (a valid share may have no amount).
 *  `measured_outstanding` is the denominator: the pool that reported a DPD, not
 *  the whole pool. */
export type DealFacilityArrears = {
  status: "MEASURED" | "NOT_MEASURED" | "UNABLE_TO_EVALUATE";
  ratio: number | null;
  amount_status: "STATED" | "NOT_SEALED" | "NOT_MEASURED" | "UNABLE_TO_EVALUATE";
  amount_native: number | null;
  amount: number | null;
  measured_outstanding_native: number | null;
  measured_outstanding: number | null;
  reason: string | null;
};

/** The contract's overcollateralisation: `(Σ eligible collateral + Σ contributed
 *  cash) ÷ what the contract has drawn`, in the contract's currency. A ratio of
 *  sums (pools are disjoint), never an average of facility ratios.
 *
 *  Carries no verdict: covenants test each facility's own OC, and one facility's
 *  collateral does not cure another's deficit. Render the census beside `value`.
 *
 *  `value` is null with `reason` whenever a side would be partial: a facility in
 *  `unmeasured_facilities` shortens the numerator (reads as less coverage), one
 *  in `excluded_facilities` (another currency) shortens the denominator (reads
 *  as more). */
export type DealContractOc = {
  value: number | null;
  /** What each facility's run counted as collateral (its OC times what it
   *  owed), summed: the numerator of `value`. */
  collateral?: number | null;
  /** The contract's OC numerator: eligible after caps plus cash, or eligible
   *  pledged loans before caps. */
  numerator?: "net_plus_cash" | "eligible_pledged";
  /** The workings, so the ratio can be checked without a second call. */
  eligible_amount: number | null;
  contributed_cash: number | null;
  /** What each facility owed on the date of the run its collateral comes from,
   *  not today's `limits.drawn`. */
  drawn: number | null;
  /** The run dates `drawn` was read on; more than one when runs differ. */
  drawn_dates: string[];
  currency: string;
  facility_count: number;
  unmeasured_facilities: { facility_id: string; facility_reference: string }[];
  excluded_facilities: { facility_id: string; facility_reference: string; currency?: string }[];
  reason: string | null;
};

/** What the contract's DSRA should hold: N months of interest (line and notes)
 *  looking forward from today on outstanding balances, clipped at each
 *  facility's maturity. Informative only: not a Payment Report rung, not
 *  compared against a bank balance, not funded.
 *
 *  `required` is null when the contract declares no `months` or a facility
 *  could not be sized (`pending` names them): a partial total would understate
 *  the reserve. */
export type DealDsra = {
  months: number | null;
  currency: string;
  required: number | null;
  facilities: {
    facility_id: string;
    facility_reference: string;
    currency: string;
    line_interest: number | null;
    note_interest: number | null;
    required: number | null;
    window: { from: string; to: string } | null;
    gap: string | null;
  }[];
  pending: { facility_id: string; facility_reference: string; gap: string | null }[];
  excluded_currencies: { facility_id: string; facility_reference: string; currency: string }[];
  gap: string | null;
};

/** The contract's ceiling, what is drawn against it and what is left, in the
 *  contract's own currency (a signed term, never converted).
 *
 *  `drawn` and `available` are null, not zero, when a facility reports in
 *  another currency: the ceiling is then reported unenforceable.
 *  `excluded_facilities` names them. */
export type DealContractLimits = {
  maximum: number | null;
  drawn: number | null;
  /** The ceiling less what is drawn. */
  available: number | null;
  /** The committed lines less what is drawn on them. */
  undrawn_commitment?: number | null;
  /** What may be drawn today: per line, the lesser of its undrawn commitment
   *  and its borrowing base less drawn; 0 while a stop_revolving is engaged.
   *  Not shown on screen. */
  available_to_draw?: number | null;
  /** Σ of the launched facilities' commitments; DRAFTs excluded. */
  committed: number;
  currency: string;
  excluded_facilities: { facility_id: string; facility_reference: string; currency: string }[];
  /** Breakdown of the totals. `drawn` sums to the total only while
   *  `excluded_facilities` is empty; an excluded facility carries `drawn: null`
   *  (it has a draw in another currency; zero would say it has not drawn). */
  facilities: {
    facility_id: string;
    facility_reference: string;
    currency: string;
    committed: number;
    drawn: number | null;
    undrawn_commitment?: number | null;
    available_to_draw?: number | null;
  }[];
};

/** One facility's share of the contract's cash. `available_funds` is what its
 *  cascade may distribute this period: every account credit with one live
 *  facility, only what the tape attributes to it with two or more. Null while
 *  no statement covers the period (unknown, never zero). */
export type DealCashFacility = {
  facility_id: string;
  facility_reference: string;
  facility_name: string;
  status: string;
  available_funds: number | null;
};

/** `GET /deals/{id}/cash`: the contract's cash for its latest distribution
 *  period and whose it is. Amounts are in book currency; convert once, as the
 *  aggregate does.
 *
 *  `residual`: credits with no confirmed match or matched to an unpledged loan.
 *  It funds no cascade and is returned whatever the covenants say. With one live
 *  facility it is zero by definition, and `unattributed` (unidentified cash) is
 *  the figure to read. */
export type DealCash = {
  deal_id: string;
  as_of: string;
  currency: string | null;
  period_start: string | null;
  period_end: string | null;
  period_source: "waterfall_run" | "statement" | null;
  /** False when the runs that closed this period do not share a start. */
  period_aligned: boolean;
  has_statement: boolean;
  attribution_mode: "sole_facility" | "by_pledge" | null;
  account_count: number;
  credits_total: number | null;
  attributed_total: number | null;
  residual: number | null;
  unattributed: number | null;
  facilities: DealCashFacility[];
};

/* The contract's series, ledger and covenant history. Transcribed from
 * `services/deal_series.py`, `services/deal_movements.py` and
 * `services/covenant_timeline.py`.
 */

/** One date of `GET /deals/{id}/series`.
 *
 *  A point is not always comparable to the previous one: the level steps up when
 *  a facility starts measuring (`facility_count` of `contract_facility_count`;
 *  `composition_changed` marks it). Collateral and drawn step together, so read
 *  them together; either curve alone misreports a composition change. */
export type DealSeriesPoint = {
  date: string;
  eligible_amount: number | null;
  /** Cash the originator put up: the other half of the cover's numerator. */
  contributed_cash: number | null;
  drawn: number | null;
  /** The contract's cover on this date, from the engine's OC function. Null when
   *  either side is missing (nothing drawn = undefined, not perfect health).
   *  Never derive it by dividing the amounts above. */
  oc: number | null;
  /** Why `drawn` is null: a facility in another currency could not be summed. */
  drawn_reason: string | null;
  facility_count: number;
  contract_facility_count: number;
  composition_changed: boolean;
  period_aligned: boolean;
  fx: DealAggregateFx;
  facilities: {
    facility_id: string;
    facility_reference: string;
    facility_name: string;
    metric_date: string | null;
    eligible_amount: number | null;
    eligible_amount_native: number | null;
    drawn: number | null;
    /** Its own cover as its run measured it; not derived from the amounts beside it. */
    oc_ratio: number | null;
  }[];
  excluded_facilities: {
    facility_id: string; facility_reference: string; facility_name: string; currency: string;
  }[];
};

/** A date where the contract's composition changed. */
export type DealSeriesChange = {
  date: string;
  facility_count: number;
  previous_count: number;
  contract_facility_count: number;
  entering: { facility_id: string; facility_reference: string; facility_name: string }[];
  leaving: { facility_id: string; facility_reference: string; facility_name: string }[];
};

/** `GET /deals/{id}/series`: the contract's collateral and drawn amounts on
 *  every date any of its facilities was measured. */
export type DealSeries = {
  deal_id: string;
  as_of: string;
  currency: string;
  native_currency: string;
  facility_count: number;
  non_active_count: number;
  point_count: number;
  first_date: string | null;
  last_date: string | null;
  /** False when the number of contributing facilities changes in the series;
   *  a chart must then say so. */
  composition_stable: boolean;
  composition_changes: DealSeriesChange[];
  points: DealSeriesPoint[];
};

/** One row of the contract's ledger. `book`: which of the four ledgers; `kind`:
 *  what it is. `outstanding_effect` is the change to what the contract owes,
 *  exactly 0 for contributed cash (collateral support, not debt). `counter`:
 *  which header total the row feeds. */
export type DealMovement = {
  id: string;
  /** Null for a note issued on a facility with no disbursement date (not today). */
  effective_date: string | null;
  facility_id: string;
  facility_reference: string;
  facility_name: string;
  currency: string;
  kind: string;
  book: string;
  amount: number;
  outstanding_effect: number;
  counter: string;
  /** Operator free text. Null under an anonymous grant (free text cannot be
   *  masked). */
  note: string | null;
  running_outstanding: number | null;
  running_outstanding_reason: string | null;
  derived_from_balance?: boolean;
  interest_paid?: number | null;
  purpose?: string | null;
};

/** A header total, its components and why it may be unknown. */
export type DealMovementTotal = {
  value: number | null;
  reason: string | null;
  line_draws?: number;
  note_issuance?: number;
  line_repayments?: number;
  note_amortisation?: number;
  contributions?: number;
  withdrawals?: number;
};

/** `GET /deals/{id}/movements`: the four ledgers in one sequence, newest first.
 *  `reconciliation` compares `outstanding` (as `limits` publishes it, never
 *  recomputed) with `closing_balance` (this ledger's own walk); when they differ
 *  `agrees` is false with the gap and why. */
export type DealMovements = {
  deal_id: string;
  as_of: string;
  currency: string;
  facility_count: number;
  non_active_count: number;
  measurable: boolean;
  excluded_facilities: { facility_id: string; facility_reference: string; currency: string }[];
  undated_issuances: { facility_id: string; facility_reference: string }[];
  totals: {
    outstanding: number | null;
    disbursed: DealMovementTotal;
    amortised: DealMovementTotal;
    /** Its own counter, outside the debt. */
    contributed_cash: DealMovementTotal;
  };
  reconciliation: {
    closing_balance: number | null;
    outstanding: number | null;
    /** Null = nothing to check against; neither agreement nor disagreement,
     *  never collapse it to false. */
    agrees: boolean | null;
    difference: number | null;
    reason: string | null;
  };
  row_count: number;
  truncated: boolean;
  movements: DealMovement[];
};

/** One band of the covenant history: one determination date with the worst
 *  verdict any facility produced that day. Verdicts are never carried forward;
 *  `measured_count` of `defining_count` shows a band with nothing measured. */
export type DealCovenantBand = {
  determination_date: string;
  status: DealCovenantStatus;
  defining_count: number;
  measured_count: number;
  breached_count: number;
  status_counts: Record<DealCovenantStatus, number>;
};

/** `GET /deals/{id}/covenant-timeline`. No values or thresholds: facilities
 *  measured against different floors share no number. Value curves live in
 *  each facility's detail. */
export type DealCovenantTimeline = {
  deal_id: string;
  as_of: string;
  since: string | null;
  facility_count: number;
  non_active_count: number;
  metric_count: number;
  metrics: {
    metric: string;
    label: string;
    defining_count: number;
    band_count: number;
    bands: DealCovenantBand[];
  }[];
};

/** One facility of `POST /deals`. Creation makes the contract and at least one
 *  facility in a single transaction: a Deal is never empty. */
export type NewDealFacility = {
  facility_name: string;
  facility_reference: string;
  currency?: string;
  facility_type?: string;
  max_facility_size?: number;
  note_balance?: number;
  note_rate?: number;
  disbursement_date?: string;
  maturity_date?: string;
  reinvestment_end_date?: string;
  advance_rate?: number;
  /** Values the originator writes in the tape's pledged_to column for this
   *  facility (need not be facility_reference). The only thing read at ingest:
   *  with none, the facility claims no loans and activation refuses it. */
  pledge_identifiers?: string[];
};

export type FacilitySummaryEntry = {
  id: string;
  facility_reference: string;
  facility_name: string;
  borrower: { id: string; legal_name: string; country: string | null; kyc_status: string } | null;
  book_id: string | null;
  /** The contract this facility lives under. */
  deal_id: string;
  /** Figures from the book's latest tape snapshot (absent when no tape is loaded). */
  book_loan_count?: number;
  book_outstanding?: number | null;
  book_snapshot_date?: string;
  status: string;
  currency: string | null;          // the facility's currency: unit of `metrics`/`history` money figures
  book_currency?: string | null;    // book currency: unit of book_outstanding and of analytics sections
  facility_type: string;
  max_facility_size: number | null;
  current_advance_rate: number | null;
  current_utilisation: number | null;
  updated_at: string | null;
  metrics: Metrics | null;
  alert_count: number;
  history: Metrics[];
};

export type OverviewSeriesBook = {
  book_id: string;
  currency: string;
  points: Array<{ date: string; outstanding: number; loans: number }>;
};

export type OverviewSeriesResponse = {
  books: OverviewSeriesBook[];
  /** Books whose newest snapshot is not summarised yet and was too large to sum live:
   *  their latest point is missing until the pipeline runs. */
  pending: string[];
};

export type DpdBucketKey = "current" | "dpd_1_30" | "dpd_31_60" | "dpd_61_90" | "dpd_90_plus";

export type OverviewAnalyticsResponse = {
  /** People with a live loan, summed across books. Per book (schemas/debtors.py):
   *  the operator's stated figure if any, else the tape's count. */
  unique_debtors: number;
  /** Books that contributed a figure meaning people. */
  debtor_books: number;
  /** Books left out: no debtor ID in the tape, or one id per loan (the count
   *  would be operations, not people). */
  debtor_books_excluded: number;
  /** Raw per (book, month, asset type); the frontend picks the dimension. */
  originations: Array<{ month: string; book_id: string; currency: string; asset_type: string | null; volume: number; loans: number }>;
  /** Daily, by exact origination date (loans-under-analytics hero). */
  originations_daily: Array<{ date: string; book_id: string; currency: string; asset_type: string | null; loans: number; volume: number }>;
  collections: Array<{ month: string; book_id: string; currency: string; principal: number; income: number }>;
  delinquency: Array<{ book_id: string; currency: string; date: string; bucket: DpdBucketKey; outstanding: number }>;
  /** Outstanding + loans per (book, snapshot, asset type); null type = not in tape. */
  asset_mix: Array<{ book_id: string; currency: string; date: string; asset_type: string | null; outstanding: number; loans: number }>;
};

export type AlertRecord = {
  id: string; type: string; severity: "CRITICAL" | "WARNING"; message: string;
  payload: Record<string, unknown> | null; acknowledged: boolean;
  acknowledged_at: string | null; created_at: string | null;
};

export type AlertsResponse = { alerts: AlertRecord[]; total: number };

export type BankAccountInfo = {
  id: string; deal_id: string; account_ref: string;
  currency: string | null; purpose: "collections" | "reserve" | "operating" | "transit";
  label: string | null; created_by: string | null; created_at: string | null;
  /** Transit accounts only. `fee_pct` is a fraction (0.02 = 2 %); `fee_fixed`
   *  an amount per collection in the account's currency. */
  channel: "operation" | "originator" | null;
  fee_pct: number | null;
  fee_fixed: number | null;
  transactions_count?: number;
};

/** Canonical field → column of the file. */
export type StatementColumnMap = {
  transaction_date: string | null; value_date: string | null;
  amount: string | null; debit: string | null; credit: string | null;
  gross: string | null; net: string | null;
  reference: string | null; counterparty: string | null; description: string | null;
  currency: string | null;
  /** Read through the source's `status_values`; a source saved without it has no key. */
  status?: string | null;
};

/** Every row to `account_id`, or `column` + `values` (cell value → account id).
 *  By column, rows whose cell is empty or in `ignored_values` are discarded;
 *  any other cell `values` does not name rejects the file on upload. */
export type StatementRouting = {
  account_id: string | null;
  column: string | null;
  values: Record<string, string>;
  /** Absent on a source saved without it. */
  ignored_values?: string[];
};

export type StatementSourceConfig = {
  reader: "tabular" | "mt940";
  encoding: string;
  separator: string;
  sheet: string | number | null;
  skip_rows: number;
  date_format: string | null;
  number_format: "1,234.56" | "1.234,56" | "1.234" | "1234.56" | null;
  sign: "positive_is_credit" | "positive_is_debit" | "all_credit" | "all_debit";
  columns: StatementColumnMap | null;
  routing: StatementRouting;
  key_columns: string[];
  signature: string[];
  /** Cell of the `status` column, compared stripped → movement status. A cell
   *  it does not name reads as no status. Absent on a source saved without it. */
  status_values?: Record<string, MovementStatus>;
};

export type StatementSource = {
  id: string; deal_id: string; name: string;
  config: StatementSourceConfig;
  created_by: string | null; created_at: string | null; updated_at: string | null;
};

export type StatementSourceRef = { id: string; name: string };

/** A parsed movement, as the preview returns it. */
export type StatementPreviewRow = {
  transaction_date: string | null;
  value_date: string | null;
  amount: number | null;
  direction: "CREDIT" | "DEBIT";
  gross_amount: number | null;
  net_amount: number | null;
  reference: string | null;
  description: string | null;
  counterparty: string | null;
  movement_key: string | null;
  /** From the source's status column and its map; null without one or for an unmapped value. */
  status: MovementStatus | null;
  account_id: string | null;
  account_ref: string | null;
  raw: Record<string, unknown> | null;
};

export type MovementStatus = "confirmed" | "pending";

export type StatementPreview = {
  deal_id: string;
  file: string | null;
  /** `id` and `name` are null for a proposed (unsaved) source. */
  source: { id: string | null; name: string | null; reader: "tabular" | "mt940"; detected: boolean };
  columns: { received: string[]; expected: string[]; missing: string[]; unmapped: string[] };
  rows_read: number;
  movements: number;
  skipped_rows: number;
  discarded_by_routing: number;
  /** Routing cell value → rows that value discarded. */
  discarded_values: Record<string, number>;
  /** Non-empty routing values the source neither routes nor ignores. While
   *  not empty, an upload of this file with this source is rejected. */
  unknown_routing_values: string[];
  /** Status cell the source's map does not name ("" when blank) → rows read without status. */
  unmapped_status_values: Record<string, number>;
  accounts: {
    id: string; account_ref: string; purpose: BankAccountInfo["purpose"];
    channel: BankAccountInfo["channel"]; movements: number; credits: number; debits: number;
  }[];
  rows: StatementPreviewRow[];
  discarded_rows: StatementPreviewRow[];
};

export type DealStatementUpload = {
  status: "ok";
  file: string | null;
  saved_path: string;
  source: StatementSourceRef & { detected: boolean };
  total_movements: number;
  /** New movements stored; repeats of stored ones are not counted. */
  transactions_persisted: number;
  /** Stored movements found again by their key whose status, value date,
   *  gross or net changed and were updated. */
  transactions_updated: number;
  accounts: {
    id: string; account_ref: string; purpose: BankAccountInfo["purpose"];
    channel: BankAccountInfo["channel"]; movements: number; transactions_persisted: number;
    /** Null in an account the file brings no movement to and declares a period for. */
    transactions_updated: number; first_date: string | null; last_date: string | null;
    /** The period the file declares it covers in the account. */
    covers_from: string; covers_to: string;
  }[];
  discarded_by_routing: number;
  discarded_values: Record<string, number>;
  skipped_rows: number;
  unmapped_status_values: Record<string, number>;
  warnings: string[];
  /** The Deal's reconciliation, recomputed after every upload. */
  reconciliations: DealAccountReconciliation[];
  sweeps: DealSweepsRun | { error: string };
  ledger: DealLedgerRefresh;
};

/** The Deal reconciliation of one transit or collections account, which
 *  persists its crosses: its summary and the rows stored, or the error that
 *  kept it from running. A collections account is classified first
 *  (`classification`) and only its unclassified credits are crossed. A
 *  recalculation lists every crossed account of the Deal. */
export type DealAccountReconciliation = {
  account_id: string; account_ref: string; purpose: BankAccountInfo["purpose"];
  channel: BankAccountInfo["channel"]; classification?: TrustClassificationRun;
  summary?: DealDryRunSummary; persisted?: DealReconciliationPersisted; error?: string;
};

/** The classification rules applied to a collections account: movements per
 *  category, the ones left unclassified, the ones classified by hand (never
 *  touched), the ones a rule matches but the analyst already crossed (left
 *  unclassified) and how many changed. */
export type TrustClassificationRun = {
  account_id: string;
  rules: number;
  movements: number;
  classified: Record<TrustCategory, number>;
  unclassified: number;
  manual: number;
  kept_crossed: number;
  changed: number;
};

/** Where a trust movement lands: its category or, for an unclassified credit,
 *  `direct_income` (in a confirmed Deal cross) or `pending_identification`;
 *  an unclassified debit is `unclassified`. Null when its category does not
 *  fit its direction. */
export type TrustBucket =
  | TrustCategory
  | "direct_income"
  | "pending_identification"
  | "unclassified";

/** A movement of the trust account with its classification. */
export type TrustTransaction = {
  bank_transaction_id: string;
  account_id: string;
  transaction_date: string | null;
  value_date: string | null;
  direction: "CREDIT" | "DEBIT";
  amount: number;
  reference: string | null;
  description: string | null;
  counterparty: string | null;
  category: TrustCategory | null;
  /** `manual` stays as set by hand, with or without category; rules never
   *  overwrite it. */
  category_source: "rule" | "manual" | null;
  /** The gateway's transit account of a gateway sweep. */
  source_account_id: string | null;
  subtype: string | null;
  bucket: TrustBucket | null;
  /** An unclassified credit a pending proposal waits on. */
  proposed: boolean;
};

export type TrustAccountRef = { id: string; account_ref: string; currency: string | null; label: string | null };

/** `GET /deals/{id}/reconciliation/trust/transactions`. */
export type TrustTransactionsPage = {
  deal_id: string;
  account: TrustAccountRef;
  from: string | null;
  to: string | null;
  bucket: TrustBucket | null;
  total: number;
  limit: number;
  offset: number;
  transactions: TrustTransaction[];
};

/** `GET /deals/{id}/reconciliation/trust`: inflows and outflows of the trust
 *  account by category. `identity` holds by construction (409 otherwise). */
export type TrustSummary = {
  deal_id: string;
  account: TrustAccountRef;
  from: string | null;
  to: string | null;
  inflows: {
    total: DryRunTally;
    gateway_sweep: DryRunTally & {
      by_gateway: (DryRunTally & {
        account_id: string | null; account_ref: string | null; label: string | null;
        channel: BankAccountInfo["channel"];
      })[];
    };
    originator_sweep: DryRunTally;
    non_collection: DryRunTally & { by_subtype: (DryRunTally & { subtype: string | null })[] };
    direct_income: DryRunTally;
    /** `proposed`: the part a pending proposal waits on. */
    pending_identification: DryRunTally & { proposed: DryRunTally };
  };
  outflows: {
    total: DryRunTally;
    bank_fee_tax: DryRunTally;
    unclassified: DryRunTally;
  };
  identity: { inflows: number; categories: number; difference: number; balanced: boolean };
};

/** Body of `PUT /deals/{id}/reconciliation/trust/transactions/{tx_id}/category`.
 *  `category: null` removes the category and keeps the movement manual. */
export type TrustCategoryRequest = {
  category: TrustCategory | null;
  source_account_id?: string | null;
  subtype?: string | null;
  note?: string | null;
};

/** A credit of the transit account, as the dry run reports it. */
export type DryRunMovement = {
  bank_transaction_id: string;
  transaction_date: string | null;
  amount: number;
  reference: string | null;
  counterparty: string | null;
  description: string | null;
  /** The account's channel. */
  channel: BankAccountInfo["channel"];
};

/** A payment of the Deal's book. `facility_id` is set only when the loan was
 *  pledged on the payment date to a launched facility of this Deal, and then
 *  `of_operation` is true. */
export type DryRunPayment = {
  payment_record_id: string;
  external_loan_id: string | null;
  payment_date: string | null;
  payment_amount: number;
  facility_id: string | null;
  facility_reference: string | null;
  of_operation: boolean;
};

/** The rule behind a cross: a learned payer alias, or a matching rule. */
export type DryRunReason = "ALIAS" | "EXACT_ID" | "AMOUNT_DIFFERENCE" | "PARTIAL_ID" | "SINGLE_CANDIDATE" | "NAME";

/** A movement paired with a payment. A grouped one is a movement that pays
 *  several payments, one row each. */
export type DryRunCross = DryRunMovement & DryRunPayment & {
  method: string;
  reason: DryRunReason;
  confidence: number;
  grouped: boolean;
};

/** A movement whose reference names a loan exactly but falls outside the
 *  tolerances, against that loan's closest payment. Differences are movement
 *  minus payment: amount received − reported, days movement − payment. */
export type DryRunDifference = DryRunMovement & DryRunPayment & {
  amount_difference: number;
  days_difference: number | null;
};

export type DryRunTally = { count: number; amount: number };

/** Movement categories count movements; `unmatched_payments` counts payments.
 *  `of_operation` / `not_of_operation` split automatic + proposals by crossed
 *  payment, with the movement's cash shared out across a grouped movement. */
export type DealDryRunSummary = {
  /** Every credit of the account: `resolved` plus one movement category each. */
  credits: DryRunTally;
  /** Credits already in a confirmed match that a run keeps. */
  resolved: DryRunTally;
  automatic: DryRunTally;
  proposals: DryRunTally;
  differences: DryRunTally;
  unmatched_movements: DryRunTally;
  unmatched_payments: DryRunTally;
  of_operation: DryRunTally;
  not_of_operation: DryRunTally;
};

/** `POST /deals/{id}/reconciliation/dry-run`: a transit account's credits, or
 *  the collections account's unclassified credits, against the Deal's
 *  payments. Nothing is persisted. */
export type DealDryRun = {
  deal_id: string;
  account: {
    id: string; account_ref: string; purpose: BankAccountInfo["purpose"];
    channel: BankAccountInfo["channel"]; currency: string | null; label: string | null;
  };
  tolerances: {
    amount_tolerance_abs: number;
    amount_propose_pct: number;
    date_tolerance_days: number;
    partial_id_min_length: number | null;
    single_candidate_days: number;
    /** The calendar both day tolerances count business days in. */
    calendar: BusinessCalendarInfo;
  };
  /** The credits' dates widened by the date tolerance; null without credits. */
  payment_window: { first_date: string; last_date: string } | null;
  summary: DealDryRunSummary;
  automatic: DryRunCross[];
  proposals: DryRunCross[];
  differences: DryRunDifference[];
  unmatched_movements: DryRunMovement[];
  unmatched_payments: DryRunPayment[];
};

/** Rows a Deal reconciliation run stored, per status. */
export type DealReconciliationPersisted = { auto_confirmed: number; pending_review: number };

/** `POST /deals/{id}/reconciliation/run`: the dry run's report, persisted. */
export type DealReconciliationRun = DealDryRun & { persisted: DealReconciliationPersisted };

/** The business-day calendar of a Deal: without a country only weekends are
 *  skipped; without a time zone "today" is the UTC date. */
export type BusinessCalendarInfo = { country: string | null; timezone: string | null; weekdays_only: boolean };

/** `POST /deals/{id}/reconciliation/sweeps/run`, and `sweeps` of a Deal
 *  statement upload: what the recomputation stored per transit account.
 *  `arrived` is null on an originator account, which never sweeps to the trust. */
export type DealSweepsRun = {
  deal_id: string;
  accounts: {
    account_id: string; account_ref: string; channel: BankAccountInfo["channel"];
    settlements: number; explained: number; unexplained: number; collections_grouped: number;
    arrived: number | null;
  }[];
};

export type SweepTally = { count: number; amount: number };
export type SweepAgedTally = SweepTally & { in_transit: SweepTally; overdue: SweepTally };

/** A gateway settlement at the report's date. `collections`, `fee_expected`
 *  and `fee_difference` are null when no prefix of collections explains it.
 *  `state`: pending (not swept), not_swept (originator account), arrived,
 *  or in_transit / overdue against the sweep term. */
export type SweepSettlement = {
  bank_transaction_id: string;
  transaction_date: string;
  value_date: string | null;
  reference: string | null;
  status: MovementStatus | null;
  gross_amount: number;
  net_amount: number;
  fee: number;
  fee_expected: number | null;
  fee_difference: number | null;
  collections: number | null;
  explained: boolean;
  trust_transaction_id: string | null;
  arrived_on: string | null;
  age_business_days: number;
  state: "pending" | "not_swept" | "arrived" | "in_transit" | "overdue";
};

/** One transit account in the sweeps report. On an originator account
 *  (`swept_to_trust` false) `arrived`, `settled_not_arrived` and
 *  `not_arrived_settlements` are null. */
export type GatewaySweeps = {
  account: {
    id: string; account_ref: string; label: string | null; channel: BankAccountInfo["channel"];
    currency: string | null; fee_pct: number | null; fee_fixed: number | null;
  };
  swept_to_trust: boolean;
  collected: SweepTally;
  /** Settlements not pending. The fee figures `fee_expected` and
   *  `fee_difference` add only the explained ones. */
  settled: {
    count: number; gross: number; net: number; fee: number; fee_expected: number; fee_difference: number;
    unexplained: { count: number; gross: number; net: number };
  };
  pending_settlements: { count: number; gross: number; net: number };
  arrived: SweepTally | null;
  pending_in_gateway: {
    /** Collections not in a settled (not pending) settlement, aged from their date. */
    unsettled: SweepAgedTally;
    /** Settled and not in the trust by the date, at their net, aged from the settlement. */
    settled_not_arrived: SweepAgedTally | null;
    in_transit: SweepTally;
    overdue: SweepTally;
    total: SweepTally;
  };
  unexplained_settlements: SweepSettlement[];
  not_arrived_settlements: SweepSettlement[] | null;
  fee_differences: SweepSettlement[];
};

/** What the originator collected on pledged loans through its own
 *  collection accounts and has not swept, accumulated to the date. Without an
 *  originator account (`basis: "no_originator_account"`) the collected side is
 *  not measured: `collected_of_operation` and `pending` are null. */
export type OriginatorPendingSweep = {
  accounts: { id: string; account_ref: string; label: string | null }[];
  basis: "originator_accounts" | "no_originator_account";
  collected_of_operation: SweepTally | null;
  swept: SweepTally;
  pending: number | null;
};

/** `GET /deals/{id}/reconciliation/sweeps?as_of=`. */
export type DealSweeps = {
  deal_id: string;
  as_of: string;
  sweep_days: number;
  sweep_ref_min_length: number | null;
  calendar: BusinessCalendarInfo;
  gateways: GatewaySweeps[];
  originator: OriginatorPendingSweep;
};

/** Without a payment calendar (`no_calendar`) or a start (`no_start`) the
 *  ledger is not built and has no periods; `message` says what to configure. */
export type DealLedgerStatus = "ok" | "no_calendar" | "no_start";
export type DealLedgerPeriodStatus = "OPEN" | "PENDING_CLOSE" | "CLOSED";
export type DealLedgerItemKind = "reported_unreceived" | "unreported_credit" | "settlement_unarrived" | "sweep_no_origin";
/** A reported payment and an unreported credit are `pending` within
 *  `aging_days`; a settlement and a sweep without settlement are `in_transit`
 *  within `sweep_days`; both are `overdue` after. An item dated outside the
 *  reconciled window is `out_of_coverage`, which is not overdue. */
export type DealLedgerItemState = "pending" | "in_transit" | "overdue" | "out_of_coverage";

export type DealLedgerTally = { count: number; outstanding: number };
/** Every state is present on every kind, at zero when the kind cannot take it. */
export type DealLedgerAgedTally = DealLedgerTally & Record<DealLedgerItemState, DealLedgerTally>;

/** One side of a cross in a period: opening + movement + adjustment = matched + closing. */
type LedgerSide<Flow extends string> = { opening: number; adjustment: number; matched: number; closing: number } & Record<Flow, number>;

/** Balance of a gateway at the period's end, accumulated: `balance` is
 *  collected less settled at gross. */
export type DealLedgerGatewayBalance = {
  account_id: string; account_ref: string; label: string | null; currency: string | null;
  collected: number; settled_gross: number; settled_net: number; fees: number; balance: number;
};

/** A rebuild run after new data: its state and how many periods it stored
 *  and computed, or why it could not run. */
export type DealLedgerRefresh = { status: DealLedgerStatus; periods: number; computed: number } | { error: string };

/** Whether a period is computed. Without every file the Deal declares it is
 *  `missing_files`, and every later period is `waiting_previous`. */
export type DealLedgerCompleteness = "complete" | "missing_files" | "waiting_previous";

type DealLedgerMissingAccount = {
  account_id: string; account_ref: string; label: string | null; purpose: BankAccountInfo["purpose"];
};

/** Something a period needs and does not have: an account's statement that
 *  covers no part of the window, a balance, or a tape never loaded or whose
 *  latest cut is before the window. */
export type DealLedgerMissing =
  | (DealLedgerMissingAccount & { kind: "statement"; from: string; to: string })
  | (DealLedgerMissingAccount & { kind: "opening_balance" | "closing_balance" })
  | { kind: "payment_tape" | "loan_tape"; covered_to: string | null; needed_to: string };

/** A declared source that covers only part of a computed period: how far it
 *  covers, how far the window needs it, how far the other declared sources
 *  reach (null without others) and the dates left out. */
export type DealLedgerCoverageWarning = {
  covered_to: string;
  needed_to: string;
  others_covered_to: string | null;
  gaps: { from: string; to: string }[];
} & ((DealLedgerMissingAccount & { kind: "statement" }) | { kind: "payment_tape" | "loan_tape" });

export type DealBalanceStatus = "ok" | "difference" | "not_entered";

/** The opening typed for a period against the closing typed for the one that
 *  ends the day before. A warning only: it blocks nothing. */
export type DealBalanceChain = {
  previous_closing: number | null;
  difference: number | null;
  status: "ok" | "break" | "not_comparable";
};

/** Opening + inflows − outflows against the closing, per account. */
export type DealBalanceControl = {
  inflows: number;
  outflows: number;
  expected_closing: number | null;
  difference: number | null;
  status: DealBalanceStatus;
};

/** The balances an admin typed for an account over a period. */
export type DealAccountBalance = {
  id: string;
  opening_balance: number | null;
  closing_balance: number | null;
  note: string | null;
  entered_by: string;
  entered_at: string | null;
};

type DealLedgerPeriodBase = {
  id: string;
  rail: "bank";
  period_start: string;
  period_end: string;
  status: DealLedgerPeriodStatus;
  /** Empty on a computed period. A waiting period lists its own gaps too. */
  missing: DealLedgerMissing[];
  /** The `missing_files` period a `waiting_previous` one waits for. */
  waiting_for: { id: string; period_start: string; period_end: string } | null;
  /** The short files of a computed period; empty when it is not computed. */
  coverage_warnings: DealLedgerCoverageWarning[];
  open_items: Record<DealLedgerItemKind, DealLedgerAgedTally>;
  closed_at: string | null;
  closed_by: string | null;
};

export type DealLedgerComputedPeriod = DealLedgerPeriodBase & {
  completeness: "complete";
  /** Computed before its cut, with the data up to the build's as-of date. */
  provisional: boolean;
  reported: LedgerSide<"reported"> & { matched_by_channel: { gateway: number; originator: number; direct: number } };
  unreported: LedgerSide<"credits">;
  settlements: LedgerSide<"settled">;
  sweeps: LedgerSide<"swept">;
  gateway_balance: DealLedgerGatewayBalance[];
  originator_pending: OriginatorPendingSweep | null;
  balance_control: {
    account_id: string; account_ref: string; status: DealBalanceStatus; difference: number | null;
    chain: DealBalanceChain;
  }[];
};

export type DealLedgerUncomputedPeriod = DealLedgerPeriodBase & {
  completeness: "missing_files" | "waiting_previous";
  provisional: false;
  reported: null;
  unreported: null;
  settlements: null;
  sweeps: null;
  gateway_balance: [];
  originator_pending: null;
  balance_control: null;
};

/** A period of the Deal's ledger, `(period_start, period_end]`. `open_items`
 *  counts the items born in it still unsettled at the ledger's `as_of`. A
 *  period that is not computed has no figures. */
export type DealLedgerPeriod = DealLedgerComputedPeriod | DealLedgerUncomputedPeriod;

/** `reconciliation.expected` of the Deal config: what every period needs. */
export type DealExpectedFiles = {
  statements: "all" | string[] | null;
  balances: "all" | string[] | null;
  payment_tape: boolean;
  loan_tape: boolean;
};

/** Up to when the Deal's data reaches: the payment tape's ingest cut, the
 *  loan tape's latest cut, the dates each account's statements cover and the
 *  date of the earliest debtor collection. */
export type DealDataCoverage = {
  payment_tape: string | null;
  loan_tape: string | null;
  first_collection: string | null;
  statements: (DealLedgerMissingAccount & { ranges: { from: string; to: string }[] })[];
};

/** From the first collection to the last date every declared statement and
 *  the payment tape reach. A null end is open. */
export type DealReconciledWindow = { from: string | null; to: string | null };

/** What every ledger read carries: its state, where it starts, the payment
 *  calendar that cuts it, the term in business days and the payments dated
 *  after `as_of`, which stay out of it. */
export type DealLedgerHeader = {
  deal_id: string;
  rail: "bank";
  status: DealLedgerStatus;
  message: string | null;
  start: { date: string; source: "start_date" | "facility_start"; first_cut: string } | null;
  payment_calendar: { frequency: string; day?: number | null; weekday?: number | null } | null;
  sweep_days: number;
  aging_days: number;
  calendar: BusinessCalendarInfo;
  currencies: string[];
  as_of: string;
  future_payments: { count: number; amount: number; first_date: string | null; last_date: string | null };
  /** Null when the Deal declares nothing: every period is computed. */
  expected: DealExpectedFiles | null;
  data_coverage: DealDataCoverage;
  /** Null when the Deal declares nothing: no item is out of coverage. */
  reconciled_window: DealReconciledWindow | null;
};

/** `GET /deals/{id}/reconciliation/ledger` and its build: periods newest first. */
export type DealLedger = DealLedgerHeader & { periods: DealLedgerPeriod[] };

/** An item of the ledger: a reported payment (`RECEIVABLE`) or a movement
 *  (`SUSPENSE`) not matched yet, or matched later. `state` is null once settled. */
export type DealLedgerItem = {
  id: string;
  kind: DealLedgerItemKind;
  item_type: "RECEIVABLE" | "SUSPENSE";
  ref_type: "payment_record" | "bank_transaction";
  ref_id: string;
  external_loan_id: string | null;
  account_id: string | null;
  reference: string | null;
  description: string | null;
  counterparty: string | null;
  opened_on: string;
  amount_original: number;
  amount_settled: number;
  amount_outstanding: number;
  status: "OPEN" | "PARTIAL" | "SETTLED";
  origin_period_id: string | null;
  settled_period_id: string | null;
  age_business_days: number | null;
  state: DealLedgerItemState | null;
};

/** An account of the Deal in a period: its typed balances, if any, and the
 *  control, null while the period is not computed. */
export type DealPeriodAccountBalance = {
  account_id: string;
  account_ref: string;
  label: string | null;
  purpose: BankAccountInfo["purpose"];
  channel: BankAccountInfo["channel"];
  currency: string | null;
  balance: DealAccountBalance | null;
  control: DealBalanceControl | null;
  chain: DealBalanceChain;
};

/** `GET /deals/{id}/reconciliation/ledger/periods/{period_id}`: every item
 *  born in the period, settled ones included, and the balances per account. */
export type DealLedgerPeriodDetail = DealLedgerHeader & {
  period: DealLedgerPeriod;
  balances: DealPeriodAccountBalance[];
  items: DealLedgerItem[];
};

/** A typed balance with its account and window; `period_id` is null when the
 *  window is no longer a period of the ledger. */
export type DealAccountBalanceEntry = DealAccountBalance & {
  account_id: string;
  account_ref: string;
  label: string | null;
  purpose: BankAccountInfo["purpose"];
  period_start: string;
  period_end: string;
  period_id: string | null;
};

export type DealAccountBalanceRequest = {
  opening_balance: number | null;
  closing_balance: number | null;
  note: string | null;
};

/** `GET /deals/{id}/reconciliation/ledger/open-items`: unsettled items,
 *  overdue first, then oldest and largest. */
export type DealLedgerOpenItemsPage = {
  deal_id: string;
  rail: "bank";
  as_of: string;
  sweep_days: number;
  aging_days: number;
  calendar: BusinessCalendarInfo;
  reconciled_window: DealReconciledWindow | null;
  kind: DealLedgerItemKind | null;
  status: DealLedgerItemState | null;
  period_id: string | null;
  total: number;
  limit: number;
  offset: number;
  items: DealLedgerItem[];
};

export type DealMatchStatus = "AUTO_CONFIRMED" | "PENDING_REVIEW" | "CONFIRMED" | "REJECTED";

/** A persisted cross of a Deal's transit or collections account. The payment fields are null
 *  when the payment link was lost to a tape re-ingest; `reason` is null on a
 *  cross made by hand. `matched_by` is "engine" or the analyst's email. */
export type DealMatchRow = DryRunMovement & {
  id: string;
  account_id: string | null;
  payment_record_id: string | null;
  external_loan_id: string | null;
  payment_date: string | null;
  payment_amount: number | null;
  facility_id: string | null;
  facility_reference: string | null;
  of_operation: boolean;
  method: string;
  reason: DryRunReason | null;
  confidence: number | null;
  grouped: boolean;
  status: DealMatchStatus;
  matched_by: string;
  resolved_by: string | null;
  resolved_at: string | null;
  match_group_id: string | null;
  /** The part of the movement this row accounts for, in a split or aggregation. */
  allocated_amount: number | null;
  notes: string | null;
  created_at: string | null;
};

/** The payer alias a confirmation or manual cross taught the borrower. */
export type DealMatchAlias = { payer_key: string; debtor_ids: string[]; action: "created" | "updated" };

export type DealMatchesPage = {
  deal_id: string;
  account_id: string | null;
  status: DealMatchStatus | null;
  total: number;
  limit: number;
  offset: number;
  matches: DealMatchRow[];
};

export type DealManualMatchBody = {
  bank_transaction_ids: string[];
  payment_record_ids: string[];
  group_key?: string;
  notes?: string;
  create_alias?: boolean;
};

export type DealManualMatchResult = {
  created: number;
  match_group_id: string | null;
  matches: DealMatchRow[];
  alias: DealMatchAlias | null;
};

export type DealStatementCoverage = {
  account_id: string;
  /** Null when the account no longer exists on the Deal. */
  account_ref: string | null;
  purpose: BankAccountInfo["purpose"] | null;
  /** The dates of the file's first and last movement in the account. */
  first_date: string | null;
  last_date: string | null;
  movements: number | null;
  /** The period the file declares it covers there, which contains its
   *  movements; the ledger's completeness reads it. */
  covers_from: string | null;
  covers_to: string | null;
};

/** `PUT /deals/{id}/statements/{file_id}/coverage/{account_id}`. A null end
 *  goes back to the movement date. */
export type DealStatementCoverageRequest = { covers_from: string | null; covers_to: string | null };

export type DealStatementCoverageCorrection = {
  file_id: string;
  account_id: string;
  account_ref: string;
  first_date: string | null;
  last_date: string | null;
  movements: number | null;
  covers_from: string | null;
  covers_to: string | null;
  ledger: DealLedgerRefresh;
};

export type DealStatementFile = {
  id: string;
  original_name: string | null;
  file_size: number | null;
  row_count: number | null;
  /** `name` is null when the source was since deleted. */
  source: { id: string; name: string | null } | null;
  coverage: DealStatementCoverage[];
  validation_warnings: string[] | null;
  uploaded_by: string | null;
  uploaded_at: string | null;
};

export type DealStatementsPage = {
  deal_id: string; total: number; limit: number; offset: number;
  statements: DealStatementFile[];
};

/** Why a Deal statement could not be read. `format_mismatch`: the file lacks
 *  columns of the source; `source_required`: no saved source, or more than one,
 *  recognises its header, so one has to be chosen; `unknown_routing_values`:
 *  the routing column has values the source neither routes nor ignores, and
 *  nothing was stored. */
export type DealStatementError =
  | {
      kind: "format_mismatch"; message: string; source: StatementSourceRef;
      expected: string[]; received: string[]; missing: string[];
    }
  | {
      kind: "unknown_routing_values"; message: string; source: StatementSourceRef;
      routing_column: string; unknown_values: string[];
    }
  | {
      kind: "source_required"; message: string;
      /** Empty when no source recognises the header. */
      matching: StatementSourceRef[];
      sources: StatementSourceRef[];
    }
  | { kind: "other"; status: number; message: string; hint?: string };

export type DealStatementResult<T> = { ok: true; data: T } | { ok: false; error: DealStatementError };

/**
 * Debita's asset class, shared by the screening form and the book. The
 * canonical list is the backend's (GET /asset-classes); this union is for
 * type-checking only: render options from the endpoint, never a literal array.
 */
export type AssetClass =
  | "CONSUMER"
  | "SME"
  | "FACTORING"
  | "AUTO"
  | "PAYROLL"
  | "BNPL"
  | "OTHER";

export type AssetClassOption = { value: AssetClass; label: string };

export type ApplicationStatus = "NEW" | "IN_REVIEW" | "APPROVED" | "REJECTED";

export type OriginatorApplication = {
  id: string;
  channel: "BD_REPRESENTATIVE" | "USDT_PROGRAM" | "ONLINE" | "OTHER";
  channel_detail: string | null;
  company_name: string;
  country: string;
  asset_type: "CONSUMER" | "SME" | "FACTORING" | "AUTO" | "PAYROLL" | "BNPL" | "OTHER";
  asset_type_detail: string | null;
  portfolio_balance: number | null;
  portfolio_currency: string;
  years_operating: number | null;
  monthly_origination: number | null;
  funding_amount: number | null;
  contact_email: string;
  loan_tape_name: string | null;
  loan_tape_size: number | null;
  payment_tape_name: string | null;
  payment_tape_size: number | null;
  documents: ApplicationDocument[];
  status: ApplicationStatus;
  created_at: string | null;
};

export type ApplicationDocument = {
  id: string;
  doc_type: string;
  label: string;
  slug: string; // kebab-case path segment (single-doc download endpoint)
  name: string | null;
  size: number | null;
};

export type FileRecord = {
  id: string; file_type: string; original_name: string | undefined;
  row_count: number | null; snapshot_date: string | null;
  processing_error: string | null;
  uploaded_by: string | null; uploaded_at: string | null;
  file_size: number | null; validation_warnings: string[] | null;
};

/** The TTL table as a function, exported for `lib/apiCache.test.ts`. An unlisted
 *  prefix silently takes `TTL.default`. Order matters: the `includes` checks run
 *  first and would capture a `/deals/...` path containing one of their fragments. */
export function ttlForPath(path: string): number {
  if (path.includes("/analytics")) return TTL.analytics;
  if (path.includes("/metrics"))   return TTL.metrics;
  if (path.includes("/alerts"))    return TTL.alerts;
  if (path.includes("/files"))     return TTL.files;
  if (path.includes("/schema"))    return TTL.schema;
  if (path.startsWith("/facilities"))   return TTL.facilities;
  // `/deals` is listed explicitly and invalidated by the Deal/facility mutations.
  if (path.startsWith("/deals"))        return TTL.deals;
  return TTL.default;
}

async function cachedFetch<T = unknown>(path: string, opts?: { silent404?: boolean; silent403?: boolean }): Promise<T> {
  const hit = cacheGet(path);
  if (hit !== null) return hit as T;

  const existing = getInFlight(path);
  if (existing) return existing as Promise<T>;

  const promise = apiFetch(path, undefined, opts).then((data) => {
    cacheSet(path, data, ttlForPath(path));
    deleteInFlight(path);
    return data;
  }).catch((e) => {
    deleteInFlight(path);
    throw e;
  });

  setInFlight(path, promise);
  return promise;
}

export { cacheInvalidate };

export async function apiFetch(
  path: string,
  options?: RequestInit,
  // `silent403` is for speculative reads the grant may not cover. It suppresses
  // the toast only; the ApiError still throws. Never set it on a write.
  opts?: { silent404?: boolean; silent403?: boolean },
) {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: { ...headers(), ...options?.headers },
    });
  } catch {
    const message = "Could not connect to server";
    _onApiError?.(message, 0);
    throw new ApiError(message, 0);
  }
  if (!res.ok) {
    if (res.status === 401) {
      clearToken();
      window.location.href = "/login";
      throw new ApiError("Session expired", 401);
    }
    const body = await res.json().catch(() => ({} as Record<string, unknown>));
    const message = _extractHttpError(body, `API error ${res.status}`);
    const isBorrowerIdentity = path.startsWith("/borrower/me");
    const isSilenced404 = res.status === 404 && opts?.silent404;
    const isSilenced403 = res.status === 403 && opts?.silent403;
    if (!(res.status === 403 && isBorrowerIdentity) && !isSilenced404 && !isSilenced403) {
      _onApiError?.(message, res.status);
    }
    throw new ApiError(message, res.status);
  }
  const ct = res.headers.get("content-type") ?? "";
  if (!ct.includes("application/json")) {
    const message = `Server returned non-JSON response (${ct || "unknown type"}) for ${path}`;
    _onApiError?.(message, res.status);
    throw new ApiError(message, res.status);
  }
  return res.json();
}

export async function apiUpload(
  path: string,
  file: File,
  extraParams?: Record<string, string>,
  /** Defaults to POST. PUT for single-slot resources that overwrite (borrower logo). */
  method: "POST" | "PUT" = "POST",
) {
  const form = new FormData();
  form.append("file", file);
  const params = extraParams ? "?" + new URLSearchParams(extraParams).toString() : "";
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}${params}`, {
      method,
      headers: headers(),
      body: form,
    });
  } catch {
    const message = "Could not connect to server";
    _onApiError?.(message, 0);
    throw new ApiError(message, 0);
  }
  if (!res.ok) {
    if (res.status === 401) {
      clearToken();
      window.location.href = "/login";
      throw new ApiError("Session expired", 401);
    }
    const body = await res.json().catch(() => ({} as Record<string, unknown>));
    const message = _extractHttpError(body, `Upload error ${res.status}`);
    _onApiError?.(message, res.status);
    throw new ApiError(message, res.status);
  }
  return res.json();
}

// apiUploadAdmin returns the 409 body instead of throwing, so the caller can
// handle group-conflict warnings.
export type UploadConflict = {
  conflict: true;
  blocked: boolean;
  reason: string;
  message: string;
  group_id: string;
  snapshot_date: string;
  file_count?: number;
};

export async function apiUploadAdmin(
  path: string,
  file: File,
  extraParams?: Record<string, string>,
): Promise<UploadResult | UploadConflict> {
  const form = new FormData();
  form.append("file", file);
  const params = extraParams ? "?" + new URLSearchParams(extraParams).toString() : "";
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}${params}`, {
      method: "POST",
      headers: headers(),
      body: form,
    });
  } catch {
    const message = "Could not connect to server";
    _onApiError?.(message, 0);
    throw new Error(message);
  }
  if (res.status === 409) {
    const body = await res.json().catch(() => ({} as Record<string, unknown>));
    const detail = (body.detail ?? body) as Record<string, unknown>;
    return {
      conflict: true,
      blocked: !!detail.blocked,
      reason: String(detail.reason ?? "group_ready"),
      message: String(detail.message ?? ""),
      group_id: String(detail.group_id ?? ""),
      snapshot_date: String(detail.snapshot_date ?? ""),
      file_count: typeof detail.file_count === "number" ? detail.file_count : undefined,
    } as UploadConflict;
  }
  if (res.status === 401) {
    clearToken();
    window.location.href = "/login";
    throw new Error("Session expired");
  }
  const body = await res.json().catch(() => ({} as Record<string, unknown>));
  return normalizeUploadResponse(res.status, body);
}

function _refs(value: unknown): StatementSourceRef[] {
  return Array.isArray(value)
    ? value.filter((v): v is StatementSourceRef => !!v && typeof v === "object" && typeof (v as StatementSourceRef).id === "string")
    : [];
}

function _strings(value: unknown): string[] {
  return Array.isArray(value) ? value.map(String) : [];
}

function _dealStatementError(status: number, body: Record<string, unknown>): DealStatementError {
  const detail = body.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const d = detail as Record<string, unknown>;
    const message = typeof d.message === "string" ? d.message : `Statement error ${status}`;
    if (d.error === "format_mismatch") {
      const src = d.source as StatementSourceRef | undefined;
      return {
        kind: "format_mismatch", message,
        source: { id: String(src?.id ?? ""), name: String(src?.name ?? "") },
        expected: _strings(d.expected_columns),
        received: _strings(d.received_columns),
        missing: _strings(d.missing_columns),
      };
    }
    if (d.error === "source_required") {
      return { kind: "source_required", message, matching: _refs(d.matching_sources), sources: _refs(d.sources) };
    }
    if (d.error === "unknown_routing_values") {
      const src = d.source as StatementSourceRef | undefined;
      return {
        kind: "unknown_routing_values", message,
        source: { id: String(src?.id ?? ""), name: String(src?.name ?? "") },
        routing_column: String(d.routing_column ?? ""),
        unknown_values: _strings(d.unknown_values),
      };
    }
    return { kind: "other", status, message, hint: typeof d.hint === "string" ? d.hint : undefined };
  }
  return { kind: "other", status, message: _extractHttpError(body, `Statement error ${status}`) };
}

// Deal statement reads answer their validation errors in a structured body the
// screen renders in place, so they return them instead of throwing and raise no
// toast. A lost connection still throws.
async function apiDealStatementPost<T>(
  path: string,
  file: File,
  params: Record<string, string | number | undefined>,
): Promise<DealStatementResult<T>> {
  const form = new FormData();
  form.append("file", file);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}${qs(params)}`, { method: "POST", headers: headers(), body: form });
  } catch {
    const message = "Could not connect to server";
    _onApiError?.(message, 0);
    throw new ApiError(message, 0);
  }
  if (res.status === 401) {
    clearToken();
    window.location.href = "/login";
    throw new ApiError("Session expired", 401);
  }
  const body = await res.json().catch(() => ({} as Record<string, unknown>));
  if (!res.ok) return { ok: false, error: _dealStatementError(res.status, body) };
  return { ok: true, data: body as T };
}


// Public screening form (/apply), unauthenticated: no Authorization header, no
// login redirect, no global toast. XHR instead of fetch for upload progress.
export function apiSubmitApplication(
  form: FormData,
  onProgress?: (pct: number) => void,
): Promise<{ id: string; status: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE}/apply`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      let body: Record<string, unknown> = {};
      try {
        body = JSON.parse(xhr.responseText) as Record<string, unknown>;
      } catch {
        // non-JSON body — handled by status check below
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as { id: string; status: string });
      } else {
        reject(new ApiError(_extractHttpError(body, `Submission error ${xhr.status}`), xhr.status));
      }
    };
    xhr.onerror = () => reject(new ApiError("Could not connect to server", 0));
    xhr.send(form);
  });
}

/** Query string from the parameters that have a value. Omitted ones are left
 *  out, never sent as the literal "undefined". */
function qs(params: Record<string, string | number | undefined>): string {
  const pairs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return pairs.length ? `?${pairs.join("&")}` : "";
}

export const api = {
  // Auth
  generatePat: (durationDays: number): Promise<{ token: string; email: string; expires_at: string; duration_days: number }> =>
    apiFetch("/auth/pat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ duration_days: durationDays }),
    }),

  exchangeAuth0Token: async (accessToken: string): Promise<{ access_token: string; email: string }> => {
    let res: Response;
    try {
      res = await fetch(`${API_BASE}/auth/token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ access_token: accessToken }),
      });
    } catch {
      throw new Error("Could not connect to server.");
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail = (data as Record<string, unknown>)?.detail;
      if (detail && typeof detail === "object") {
        const d = detail as Record<string, unknown>;
        throw new ApiError(
          typeof d.message === "string" ? d.message : "Access denied.",
          res.status,
          typeof d.error === "string" ? d.error : undefined,
        );
      }
      throw new ApiError(typeof detail === "string" ? detail : "Access denied.", res.status);
    }
    return data;
  },

  // Borrower upload section
  borrowerInfo: () => apiFetch(`/borrower/me`),
  borrowerDeleteFile: (fileId: string) =>
    apiFetch(`/borrower/me/files/${fileId}`, { method: "DELETE" }),
  borrowerDownloadFile: async (fileId: string, fileName: string) => {
    const res = await fetch(`${API_BASE}/borrower/me/download/${fileId}`, { headers: headers() });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  },
  borrowerUploadFile: async (
    file: File,
    fileType: string,
    snapshotDate: string,
    signal?: AbortSignal,
  ): Promise<UploadResult> => {
    const form = new FormData();
    form.append("file", file);
    const params = `?file_type=${encodeURIComponent(fileType)}&snapshot_date=${encodeURIComponent(snapshotDate)}`;
    let res: Response;
    try {
      res = await fetch(`${API_BASE}/borrower/me/upload${params}`, {
        method: "POST",
        headers: headers(),
        body: form,
        signal,
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") throw err;
      throw new Error("Could not connect to server");
    }
    if (res.status === 401) {
      clearToken();
      window.location.href = "/login";
      throw new Error("Session expired");
    }
    const body = await res.json().catch(() => ({} as Record<string, unknown>));
    return normalizeUploadResponse(res.status, body);
  },

  // Health
  health: () => apiFetch("/health"),

  // Originator applications (public /apply screening form; admin review)
  listApplications: (): Promise<{ applications: OriginatorApplication[]; total: number; new_count: number }> =>
    apiFetch("/applications"),
  getApplication: (id: string): Promise<OriginatorApplication> => apiFetch(`/applications/${id}`),
  updateApplicationStatus: (id: string, status: ApplicationStatus): Promise<OriginatorApplication> =>
    apiFetch(`/applications/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    }),
  downloadApplicationFile: async (id: string, kind: string, filename: string) => {
    const res = await fetch(`${API_BASE}/applications/${id}/files/${kind}`, { headers: headers() });
    if (res.status === 401) { clearToken(); window.location.href = "/login"; throw new Error("Session expired"); }
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(blobUrl);
  },
  downloadApplicationDocument: async (appId: string, documentId: string, filename: string) => {
    const res = await fetch(`${API_BASE}/applications/${appId}/documents/${documentId}`, { headers: headers() });
    if (res.status === 401) { clearToken(); window.location.href = "/login"; throw new Error("Session expired"); }
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(blobUrl);
  },

  // Borrowers
  listBorrowers: () => apiFetch("/borrowers"),
  getBorrower: (id: string) => apiFetch(`/borrowers/${id}`),
  createBorrower: (body: {
    legal_name: string;
    currency: string;
    aliases?: string[] | null;
    country?: string | null;
    jurisdiction?: string | null;
    kyc_status?: "PENDING" | "APPROVED" | "REJECTED";
    kyc_metadata?: Record<string, unknown> | null;
  }) =>
    apiFetch("/borrowers", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  updateBorrower: (id: string, body: Record<string, unknown>) =>
    apiFetch(`/borrowers/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  deleteBorrower: (id: string, force = false) =>
    apiFetch(`/borrowers/${id}${force ? "?force=true" : ""}`, { method: "DELETE" }),

  /**
   * Every borrower's logo as a data URI, keyed by id; one cached call feeds every
   * table and goes straight into <img src>. Borrowers without a logo are absent.
   */
  getBorrowerLogos: () => cachedFetch<{ logos: Record<string, string> }>(`/borrowers/logos`),
  /** PUT: a borrower has one logo; re-uploading overwrites it. */
  uploadBorrowerLogo: async (id: string, file: File) => {
    const res = await apiUpload(`/borrowers/${id}/logo`, file, undefined, "PUT");
    // Otherwise tables keep the old logo for up to the cache TTL.
    cacheInvalidate("/borrowers/logos");
    return res;
  },
  deleteBorrowerLogo: async (id: string) => {
    const res = await apiFetch(`/borrowers/${id}/logo`, { method: "DELETE" });
    cacheInvalidate("/borrowers/logos");
    return res;
  },

  /**
   * The originator's presentation card. Readable by admins and by anyone with a
   * permission on one of the borrower's books (book Profile tab, facility
   * Originator block), unlike the admin-only borrower detail endpoint.
   */
  getBorrowerProfile: (id: string) =>
    cachedFetch<BorrowerProfileResponse>(`/borrowers/${id}/profile`),
  /** PUT: the editor replaces the whole document. Admin only. */
  updateBorrowerProfile: async (id: string, body: BorrowerProfileInput) => {
    const res = await apiFetch(`/borrowers/${id}/profile`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    // Otherwise read surfaces keep the previous card for up to the cache TTL.
    cacheInvalidate(`/borrowers/${id}/profile`);
    return res as BorrowerProfileResponse;
  },

  /**
   * The originator's own accounts. Same read rule as the profile. Ratios and
   * coherence checks arrive computed; never recompute them in the client.
   */
  getBorrowerFinancials: (id: string) =>
    cachedFetch<BorrowerFinancialsResponse>(`/borrowers/${id}/financials`),
  deleteBorrowerFinancialsPeriod: async (id: string, periodId: string) => {
    const res = await apiFetch(`/borrowers/${id}/financials/${periodId}`, { method: "DELETE" });
    cacheInvalidate(`/borrowers/${id}/financials`);
    return res;
  },

  /** The statements as delivered. Admin only (they carry letterhead, company
   *  number and the auditor's signature). */
  getFinancialDocuments: (id: string) =>
    cachedFetch<{ documents: FinancialDocumentRecord[] }>(`/borrowers/${id}/financial-documents`),
  uploadFinancialDocument: async (
    id: string,
    file: File,
    docType: string,
    periodLabel?: string,
  ) => {
    const res = await apiUpload(`/borrowers/${id}/financial-documents`, file, {
      doc_type: docType,
      ...(periodLabel ? { period_label: periodLabel } : {}),
    });
    cacheInvalidate(`/borrowers/${id}/financial-documents`);
    return res as FinancialDocumentRecord;
  },
  downloadFinancialDocument: async (id: string, documentId: string, filename: string) => {
    const res = await fetch(`${API_BASE}/borrowers/${id}/financial-documents/${documentId}/download`, {
      headers: headers(),
    });
    if (res.status === 401) { clearToken(); window.location.href = "/login"; throw new Error("Session expired"); }
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(blobUrl);
  },

  // Books
  /** Debita's asset-class vocabulary. Static, so cached; never hardcode the list. */
  getAssetClasses: () =>
    cachedFetch<{ asset_classes: AssetClassOption[] }>(`/asset-classes`),
  listBorrowerBooks: (borrowerId: string) => apiFetch(`/borrowers/${borrowerId}/books`),
  getBook: (id: string) => apiFetch(`/books/${id}`),
  // Analyst notes on a book, keyed to the tab they were written on. Admin-only
  // on the server.
  getBookNotes: (id: string) => apiFetch(`/books/${id}/notes`),
  addBookNote: (id: string, section: string, body: string, visibility: "internal" | "external" = "internal") =>
    apiFetch(`/books/${id}/notes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ section, body, visibility }),
    }),
  deleteBookNote: (id: string, noteId: string) =>
    apiFetch(`/books/${id}/notes/${noteId}`, { method: "DELETE" }),
  getBookAnalytics: (id: string, snapshotDate?: string) =>
    apiFetch(`/books/${id}/analytics${snapshotDate ? `?snapshot_date=${snapshotDate}` : ""}`),
  /** The book's analytics for a reader who may hold only a contract grant: the
   *  endpoint asks for `book_view`, so a 403 is an answer here, not an error
   *  worth a toast. The caller renders «not shared». */
  getBookAnalyticsIfShared: (id: string) =>
    cachedFetch<Record<string, unknown>>(`/books/${id}/analytics`, { silent403: true, silent404: true }),
  getBookProposedStructure: (id: string) =>
    apiFetch(`/books/${id}/proposed-structure`),
  stressSimulate: (facilityId: string, body: Record<string, unknown>) =>
    apiFetch(`/facilities/${facilityId}/stress-simulate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  getBookSnapshotDates: (id: string) =>
    cachedFetch<string[]>(`/books/${id}/analytics/available-dates`),
  getBookMetricsHistory: (id: string) =>
    cachedFetch<Record<string, unknown>[]>(`/books/${id}/metrics/history`),
  getBookLoans: (
    id: string,
    params: {
      dpd_min?: number;
      dpd_max?: number;
      lifecycle_status?: string;
      exclude_status?: string;
      order_by?: string;
      limit?: number;
      offset?: number;
      snapshot_date?: string;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) qs.append(k, String(v));
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return cachedFetch<{
      book_id: string;
      snapshot_date: string | null;
      total: number;
      limit?: number;
      offset?: number;
      loans: Array<{
        loan_id: string;
        external_loan_id: string;
        debtor_id: string | null;
        debtor_name: string | null;
        outstanding: number;
        original_amount: number | null;
        /** null = the tape reported no DPD for this loan (not 0). */
        dpd: number | null;
        bucket: string;
        vintage_month: string | null;
        lifecycle_status: string;
      }>;
    }>(`/books/${id}/loans${suffix}`);
  },
  createBook: (
    borrowerId: string,
    body: {
      currency: string;
      tape_mapping_config: Record<string, unknown>;
      payment_mapping_config: Record<string, unknown>;
      customer_mapping_config?: Record<string, unknown> | null;
      collateral_mapping_config?: Record<string, unknown> | null;
      schedule_mapping_config?: Record<string, unknown> | null;
      bank_config?: Record<string, unknown> | null;
      asset_class?: string | null;
    },
  ) =>
    apiFetch(`/borrowers/${borrowerId}/books`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  updateBook: (id: string, body: Record<string, unknown>) =>
    apiFetch(`/books/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  deleteBook: (id: string, force = false) =>
    apiFetch(`/books/${id}${force ? "?force=true" : ""}`, { method: "DELETE" }),
  runBookIngest: (id: string, body: { snapshot_date?: string | null; cascade_to_facilities?: boolean }) =>
    apiFetch(`/books/${id}/run-ingest`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  // Deals. Writers that create or delete drop both prefixes: a Deal mutation
  // changes facilities and a facility mutation changes its Deal (list, count,
  // derived status). `cacheInvalidate` clears by prefix, so "/deals" also
  // clears "/deals/{id}/...".
  listDeals: (bookId?: string) =>
    cachedFetch<DealListItem[]>(`/deals${bookId ? `?book_id=${bookId}` : ""}`),
  getDeal: (id: string) => cachedFetch<DealInfo>(`/deals/${id}`),
  createDeal: (body: {
    deal_reference: string;
    deal_name: string;
    book_id: string;
    currency: string;
    deal_config?: Record<string, unknown>;
    facilities: NewDealFacility[];
  }) =>
    apiFetch("/deals", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
      .then((r) => { cacheInvalidate("/deals"); cacheInvalidate("/facilities"); return r; }),
  updateDeal: (id: string, body: { deal_name?: string; deal_reference?: string; currency?: string }) =>
    apiFetch(`/deals/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
      .then((r) => { cacheInvalidate("/deals"); return r; }),
  updateDealConfig: (id: string, body: Record<string, unknown>) =>
    apiFetch(`/deals/${id}/config`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
      .then((r) => {
        cacheInvalidate("/deals");
        return r as {
          status: string; deal_config: DealConfig; deal_config_effective: DealConfig;
          /** The Deal's crosses and sweeps, recomputed when the reconciliation
           *  block changed; null otherwise. */
          reconciliations: DealAccountReconciliation[] | null;
          sweeps: DealSweepsRun | { error: string } | null;
          /** The ledger rebuilt when the reconciliation block or the calendar changed; null otherwise.
           *  A failed rebuild leaves the config saved. */
          ledger: DealLedgerRefresh | null;
        };
      }),
  // Admin-only. Under `/deals`, so the config PUT above invalidates it too (the
  // declared annex is part of the answer).
  getDealConfigOptions: (id: string) => cachedFetch<DealConfigOptions>(`/deals/${id}/config-options`),
  // The contract's read-models, cached like every `/deals` read. Their inputs
  // (`covenant_observations`, `daily_metrics`) are written by the pipeline, so a
  // finished run drops the prefix too (PipelineProgressModal).
  getDealCovenants: (id: string, asOf?: string) =>
    cachedFetch<DealCovenants>(`/deals/${id}/covenants${asOf ? `?as_of=${asOf}` : ""}`),
  getDealAggregate: (id: string, asOf?: string) =>
    cachedFetch<DealAggregate>(`/deals/${id}/aggregate${asOf ? `?as_of=${asOf}` : ""}`),
  // Its inputs (`bank_transactions`, confirmed matches) are written by the
  // statement upload and the analyst queue, both of which drop `/deals`.
  getDealCash: (id: string, asOf?: string) =>
    cachedFetch<DealCash>(`/deals/${id}/cash${asOf ? `?as_of=${asOf}` : ""}`),
  getDealSeries: (id: string, asOf?: string) =>
    cachedFetch<DealSeries>(`/deals/${id}/series${qs({ as_of: asOf })}`),
  getDealMovements: (id: string, asOf?: string, limit?: number) =>
    cachedFetch<DealMovements>(`/deals/${id}/movements${qs({ as_of: asOf, limit })}`),
  // Narrow to one metric when charting one covenant; the full series is large.
  getDealCovenantSeries: (id: string, metric?: string, since?: string, asOf?: string) =>
    cachedFetch<DealCovenantSeries>(
      `/deals/${id}/covenant-series${qs({ metric, since, as_of: asOf })}`,
    ),
  getDealCovenantTimeline: (id: string, since?: string, asOf?: string) =>
    cachedFetch<DealCovenantTimeline>(
      `/deals/${id}/covenant-timeline${qs({ since, as_of: asOf })}`,
    ),
  getDealReportDates: (id: string) =>
    cachedFetch<DealReportDates>(`/deals/${id}/report-dates`),
  getDealPaymentSchedule: (id: string, asOf?: string) =>
    cachedFetch<DealPaymentSchedule>(`/deals/${id}/payment-schedule${qs({ as_of: asOf })}`),
  // The contract's Payment Report: what each rung has accrued and will cost by
  // the payment date. Computed on read from the facilities' period cost, so it
  // moves when a movement is recorded, not only on a run.
  getDealPeriodCost: (id: string, asOf?: string) =>
    cachedFetch<DealPeriodLadder>(`/deals/${id}/period-cost${asOf ? `?as_of=${asOf}` : ""}`),
  // A facility's amendment history, oldest first (each version from its `effective_from`).
  getFacilityTerms: (id: string) => cachedFetch<FacilityTermHistory>(`/facilities/${id}/terms`),

  getDealAudit: (id: string) => apiFetch(`/deals/${id}/audit`) as Promise<DealAuditEntry[]>,
  // Never cached: it answers "who gains access if I add this facility".
  getDealAccess: (id: string) => apiFetch(`/deals/${id}/access`) as Promise<DealAccess>,
  deleteDeal: (id: string, force = false) =>
    apiFetch(`/deals/${id}${force ? "?force=true" : ""}`, { method: "DELETE" })
      .then((r) => { cacheInvalidate("/deals"); cacheInvalidate("/facilities"); return r; }),

  // Facilities
  listFacilities: () => cachedFetch<FacilityListItem[]>("/facilities"),
  facilitiesSummary: () => cachedFetch<FacilitySummaryEntry[]>("/facilities/summary"),
  overviewSeries: () => cachedFetch<OverviewSeriesResponse>("/facilities/overview-series"),
  overviewAnalytics: () => cachedFetch<OverviewAnalyticsResponse>("/facilities/overview-analytics"),
  hydrateFacility: (id: string) => cachedFetch<{ facility: FacilityInfo; metrics: Metrics | null; history: Metrics[]; analytics: Analytics | null }>(`/facilities/${id}/hydrate`),
  // `deal_id`, not `borrower_id`: book and borrower are read off the contract.
  createFacility: (body: NewDealFacility & { deal_id: string }) =>
    // Invalidate here, not at the call site, so a new facility shows without a
    // reload. Store-backed callers must still refresh (useGlobalStore.refreshFacilities).
    apiFetch("/facilities", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
      .then((r) => { cacheInvalidate("/facilities"); cacheInvalidate("/deals"); return r; }),
  getFacility: (id: string) => cachedFetch<FacilityInfo>(`/facilities/${id}`),
  activateFacility: (id: string) =>
    apiFetch(`/facilities/${id}/activate`, { method: "POST" })
      .then((r) => { cacheInvalidate("/facilities"); cacheInvalidate("/deals"); return r; }),

  // Schema
  loanTapeSchema: () => cachedFetch("/facilities/schema/loan-tape"),
  paymentSchema: () => cachedFetch("/facilities/schema/payment-tape"),
  scheduleSchema: () => cachedFetch("/facilities/schema/schedule"),
  customerSchema: () => cachedFetch("/facilities/schema/customer"),
  collateralSchema: () => cachedFetch("/facilities/schema/collateral"),

  // Sample analysis + AI mapping suggestion: propose a column_map, never persist.
  analyzeSample: (id: string, file: File) => apiUpload(`/facilities/${id}/analyze-sample`, file),
  suggestMapping: (id: string) => apiFetch(`/facilities/${id}/suggest-mapping`, { method: "POST" }),

  // Mapping is book-scoped: read with getBook, write with updateBook. No
  // facility-scoped mapping writer: it would replace the whole book's config.

  // Eligibility and covenants. No cascade writer: legacy step shapes are
  // migrated on read and cannot round-trip, and the order of payment is the
  // contract's, declared on the Deal.
  updateEligibility: (id: string, body: Record<string, unknown>) =>
    apiFetch(`/facilities/${id}/eligibility`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  updateCovenants: (id: string, body: Record<string, unknown>) =>
    apiFetch(`/facilities/${id}/covenants`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  // Persisted waterfall runs + instructions
  getWaterfallRuns: (id: string, params?: { from?: string; to?: string; status?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.from) q.set("from", params.from);
    if (params?.to) q.set("to", params.to);
    if (params?.status) q.set("status", params.status);
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString() ? `?${q}` : "";
    return cachedFetch<WaterfallRunsResponse>(`/facilities/${id}/waterfall/runs${qs}`);
  },
  getWaterfallRunDetail: (id: string, runId: string) =>
    cachedFetch<WaterfallRunRecord>(`/facilities/${id}/waterfall/runs/${runId}`),
  // Covenant observation series (admin-only endpoint)
  covenantObservations: (id: string, params?: { covenant_key?: string; from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (params?.covenant_key) q.set("covenant_key", params.covenant_key);
    if (params?.from) q.set("from", params.from);
    if (params?.to) q.set("to", params.to);
    const qs = q.toString() ? `?${q}` : "";
    return cachedFetch<CovenantObservationsResponse>(`/facilities/${id}/covenants/observations${qs}`);
  },
  // Acquisition-vintage cohorts: collections-to-date % of funded by pool-entry
  // vintage, derived from the pledge history.
  facilityAcquisitionCohorts: (id: string) =>
    cachedFetch<AcquisitionCohortsResponse>(`/facilities/${id}/acquisition-cohorts`),
  // Facility "At a Glance" + exposure
  facilityGlance: (id: string) =>
    cachedFetch<FacilityGlanceResponse>(`/facilities/${id}/facility-glance`),
  // Facility line drawn (ops surface). Uncached: mutated on write.
  facilityLineMovements: (id: string): Promise<LineMovementsResponse> =>
    apiFetch(`/facilities/${id}/line-movements`),
  facilityAddLineMovement: (id: string, body: { effective_date: string; kind: "DRAW" | "REPAYMENT"; amount: number; note?: string | null }) =>
    apiFetch(`/facilities/${id}/line-movements`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  facilityDeleteLineMovement: (id: string, movementId: string) =>
    apiFetch(`/facilities/${id}/line-movements/${movementId}`, { method: "DELETE" }),
  // Securitization notes (ops surface). Uncached: mutated on write.
  facilityNotes: (id: string): Promise<NoteLedger> =>
    apiFetch(`/facilities/${id}/notes`),
  /** No «create tranche»: a series is a facility, so face value, coupon and
   *  dates are facility terms. This writes the repayment ledger only. */
  facilityAddAmortisation: (id: string, body: { amort_date: string; principal_amortised: number; interest_paid?: number | null }) =>
    apiFetch(`/facilities/${id}/notes/amortisation`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  facilityDeleteAmortisation: (id: string, amortId: string) =>
    apiFetch(`/facilities/${id}/notes/amortisation/${amortId}`, { method: "DELETE" }),
  // DPD × dimension cross-tabs
  facilityDpdStratification: (id: string) =>
    cachedFetch<DpdStratificationResponse>(`/facilities/${id}/dpd-stratification`),
  // Asset test: unified collateral-coverage cascade. `asOf` reads the last
  // finished measurement on or before it.
  facilityAssetTest: (id: string, asOf?: string) =>
    cachedFetch<AssetTestResponse>(`/facilities/${id}/asset-test${asOf ? `?as_of=${asOf}` : ""}`),
  // What the current period costs, with the arithmetic behind each figure.
  facilityPeriodCost: (id: string, asOf?: string) =>
    cachedFetch<PeriodCostResponse>(
      `/facilities/${id}/period-cost${asOf ? `?as_of=${asOf}` : ""}`,
    ),
  // Incidents (admin-only endpoints)
  getFacilityIncidents: (id: string, params?: { incident_type?: string; status?: string; severity?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.incident_type) q.set("incident_type", params.incident_type);
    if (params?.status) q.set("status", params.status);
    if (params?.severity) q.set("severity", params.severity);
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString() ? `?${q}` : "";
    return cachedFetch<{ facility_id: string; total: number; incidents: IncidentRecord[] }>(`/facilities/${id}/incidents${qs}`);
  },
  // Eligibility views: substitution reserve / ceded-not-eligible.
  // 404 = no eligibility evaluation yet; the block renders a placeholder.
  getEligibilityViews: (id: string, snapshotDate?: string) => {
    const qs = snapshotDate ? `?snapshot_date=${snapshotDate}` : "";
    return cachedFetch<EligibilityViews>(`/facilities/${id}/eligibility/views${qs}`, { silent404: true });
  },
  updateFacilityConfig: (id: string, body: Record<string, unknown>) =>
    apiFetch(`/facilities/${id}/facility-config`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  // Manual re-run after editing config (never automatic on save)
  recalculateFacility: (id: string) =>
    apiFetch(`/facilities/${id}/recalculate`, { method: "POST" }) as Promise<{
      status: string;
      facility_id: string;
      mode: "determination" | "snapshot" | "draft";
    }>,
  getFacilityAudit: (id: string) => apiFetch(`/facilities/${id}/audit`),

  // Analyst notes (variable-level commentary, shown as pulse dots). Admin only,
  // in the UI and at the endpoint.
  getAnalystNotes: (id: string) =>
    apiFetch(`/facilities/${id}/analyst-notes`) as Promise<{
      facility_id: string;
      notes: Record<string, { note: string; author: string; ts: string; scope?: string | null; read_by?: string[] }>;
    }>,
  updateAnalystNotes: (
    id: string,
    notes: Record<string, { note: string; scope?: string | null } | null>,
  ) =>
    apiFetch(`/facilities/${id}/analyst-notes`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ notes }),
    }),
  markAnalystNoteRead: (id: string, variable_key: string) =>
    apiFetch(`/facilities/${id}/analyst-notes/read`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ variable_key }),
    }),

  // Metrics & Analytics
  facilityStatus: (id: string) => apiFetch(`/facilities/${id}/status`),
  facilityMetrics: (id: string) => cachedFetch<Metrics>(`/facilities/${id}/metrics/latest`),
  facilityMetricsHistory: (id: string) => cachedFetch<Metrics[]>(`/facilities/${id}/metrics/history`),
  getFacilityAnalytics: (id: string, snapshotDate?: string, eligibleOnly?: boolean) => {
    const params = new URLSearchParams();
    if (snapshotDate) params.set("snapshot_date", snapshotDate);
    if (eligibleOnly) params.set("eligible_only", "true");
    const qs = params.toString() ? `?${params}` : "";
    // eligible-scope 404s are expected for dates no eligibility-scoped facility
    // run covers; no global error toast.
    return cachedFetch<Analytics>(`/facilities/${id}/analytics${qs}`, eligibleOnly ? { silent404: true } : undefined);
  },
  getFacilitySnapshotDates: (id: string) =>
    cachedFetch<string[]>(`/facilities/${id}/analytics/available-dates`),

  /** Every stored snapshot of one analytics section, oldest first. Pledged
   *  variant only. For levels that need their history to be read. */
  getFacilityAnalyticsHistory: <T = unknown>(id: string, section: string) =>
    cachedFetch<Array<{ snapshot_date: string; result: T | null; calculated_at: string | null }>>(
      `/facilities/${id}/analytics/history/${section}`,
    ),

  /** One analytics section of a facility at a snapshot. Pledged variant only
   *  (the facility's pool, not the book). `result` null = the calculator did not
   *  run or produced nothing; render that, never an empty chart. */
  getFacilityAnalyticsSection: <T = unknown>(id: string, section: string, snapshotDate?: string) =>
    cachedFetch<FacilityAnalyticsSection<T>>(
      `/facilities/${id}/analytics/${section}${qs({ snapshot_date: snapshotDate })}`,
    ),

  // Alerts
  getAlerts: (id: string, params?: { severity?: string; acknowledged?: boolean; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.severity) q.set("severity", params.severity);
    if (params?.acknowledged !== undefined) q.set("acknowledged", String(params.acknowledged));
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString() ? `?${q}` : "";
    return cachedFetch<AlertsResponse>(`/facilities/${id}/alerts${qs}`);
  },
  getGlobalAlerts: (params?: { severity?: string; acknowledged?: boolean; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.severity) q.set("severity", params.severity);
    if (params?.acknowledged !== undefined) q.set("acknowledged", String(params.acknowledged));
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString() ? `?${q}` : "";
    return cachedFetch<(AlertRecord & { facility_name: string })[]>(`/alerts${qs}`);
  },
  acknowledgeAlert: (facilityId: string, alertId: string) =>
    apiFetch(`/facilities/${facilityId}/alerts/${alertId}/acknowledge`, { method: "POST" }),

  // Incidents: global inbox and facility tab share the global list
  // (priority-ordered server-side; the facility tab passes facility_id).
  getGlobalIncidents: (params?: {
    status?: string; incident_type?: string; severity?: string;
    facility_id?: string; limit?: number; offset?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.incident_type) q.set("incident_type", params.incident_type);
    if (params?.severity) q.set("severity", params.severity);
    if (params?.facility_id) q.set("facility_id", params.facility_id);
    if (params?.limit) q.set("limit", String(params.limit));
    if (params?.offset) q.set("offset", String(params.offset));
    const qs = q.toString() ? `?${q}` : "";
    return cachedFetch<IncidentRecord[]>(`/incidents${qs}`);
  },
  getIncidentsSummary: (params?: { facility_id?: string }) => {
    const qs = params?.facility_id ? `?facility_id=${params.facility_id}` : "";
    return cachedFetch<IncidentsSummary>(`/incidents/summary${qs}`);
  },
  getIncidentDetail: (facilityId: string, incidentId: string) =>
    apiFetch(`/facilities/${facilityId}/incidents/${incidentId}`) as Promise<IncidentDetail>,
  transitionIncident: (facilityId: string, incidentId: string, body: { status: IncidentStatus; note?: string }) =>
    apiFetch(`/facilities/${facilityId}/incidents/${incidentId}/transition`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }) as Promise<IncidentRecord>,

  // Files & Pipeline Runs (book-level)
  listFilesBook: (bookId: string, fileType?: string) => {
    const q = fileType ? `?file_type=${fileType}` : "";
    return cachedFetch<FileRecord[]>(`/books/${bookId}/files${q}`);
  },
  listUploadGroupsBook: (bookId: string) => apiFetch(`/books/${bookId}/upload-groups`),
  listPipelineRunsBook: (bookId: string, limit = 10, offset = 0) =>
    apiFetch(`/books/${bookId}/pipeline-runs?limit=${limit}&offset=${offset}`),
  getPipelineRunDetailBook: (bookId: string, runId: string) =>
    apiFetch(`/books/${bookId}/pipeline-runs/${runId}/detail`),
  getPipelineRunDetailUnified: (scopeId: string, scopeType: "book" | "facility", runId: string) =>
    scopeType === "facility"
      ? apiFetch(`/facilities/${scopeId}/pipeline-runs/${runId}/detail`)
      : apiFetch(`/books/${scopeId}/pipeline-runs/${runId}/detail`),
  deleteFileBook: (bookId: string, fileId: string) =>
    apiFetch(`/books/${bookId}/files/${fileId}`, { method: "DELETE" }),
  downloadFileBook: async (bookId: string, fileId: string, filename?: string) => {
    const { url: presignedUrl, filename: serverFilename } = await apiFetch(
      `/books/${bookId}/files/${fileId}/presigned-url`
    );
    if (presignedUrl) {
      const a = document.createElement("a");
      a.href = presignedUrl;
      a.download = filename || serverFilename || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
    const res = await fetch(`${API_BASE}/books/${bookId}/files/${fileId}/download`, {
      headers: headers(),
    });
    if (res.status === 401) { clearToken(); window.location.href = "/login"; throw new Error("Session expired"); }
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "download";
    a.click();
    URL.revokeObjectURL(blobUrl);
  },
  deleteUploadGroupBook: (bookId: string, groupId: string) =>
    apiFetch(`/books/${bookId}/upload-groups/${groupId}`, { method: "DELETE" }),
  deleteRunBook: (bookId: string, runId: string) =>
    apiFetch(`/books/${bookId}/pipeline-runs/${runId}`, { method: "DELETE" }),
  runPipelineBook: (bookId: string, opts: {
    loanTapeFileId?: string; paymentTapeFileId?: string;
    scheduleFileId?: string; customerFileId?: string; collateralFileId?: string;
    force?: boolean;
  } = {}) => {
    const params = new URLSearchParams();
    if (opts.loanTapeFileId) params.set("loan_tape_file_id", opts.loanTapeFileId);
    if (opts.paymentTapeFileId) params.set("payment_tape_file_id", opts.paymentTapeFileId);
    if (opts.scheduleFileId) params.set("schedule_file_id", opts.scheduleFileId);
    if (opts.customerFileId) params.set("customer_file_id", opts.customerFileId);
    if (opts.collateralFileId) params.set("collateral_file_id", opts.collateralFileId);
    if (opts.force) params.set("force", "true");
    return apiFetch(`/books/${bookId}/run-pipeline?${params}`, { method: "POST" });
  },
  uploadLoanTapeBook: (bookId: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/books/${bookId}/upload/loan-tape`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),
  uploadPaymentTapeBook: (bookId: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/books/${bookId}/upload/payment-tape`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),
  uploadScheduleBook: (bookId: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/books/${bookId}/upload/schedule`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),
  uploadCustomerBook: (bookId: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/books/${bookId}/upload/customer`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),
  uploadCollateralBook: (bookId: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/books/${bookId}/upload/collateral`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),

  // Files & Pipeline Runs, facility-level (@deprecated, use the book-level equivalents above)
  listFiles: (id: string, fileType?: string) => {
    const q = fileType ? `?file_type=${fileType}` : "";
    return cachedFetch<FileRecord[]>(`/facilities/${id}/files${q}`);
  },
  // Denied under anonymous grants, like the files endpoints; the Overview only
  // dates the last completed run with it, so a 403 renders as "no run".
  listPipelineRuns: (id: string, limit = 10, offset = 0) =>
    apiFetch(`/facilities/${id}/pipeline-runs?limit=${limit}&offset=${offset}`, undefined, { silent403: true }),
  getPipelineRunDetail: (facilityId: string, runId: string) => apiFetch(`/facilities/${facilityId}/pipeline-runs/${runId}/detail`),
  downloadUnmatched: (facilityId: string, runId: string) => {
    const url = `${API_BASE}/facilities/${facilityId}/pipeline-runs/${runId}/unmatched.csv`;
    return fetch(url, { headers: headers() })
      .then(r => { if (r.status === 401) { clearToken(); window.location.href = "/login"; throw new Error("Session expired"); } if (!r.ok) throw new Error("No unmatched data"); return r.blob(); })
      .then(blob => {
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objUrl;
        a.download = `unmatched_${runId.slice(0, 8)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
      });
  },

  // Facility mutations drop both the facility and deal caches: the Deal payload
  // carries its facilities, their count and the derived status.
  updateFacility: (id: string, body: Record<string, unknown>) =>
    apiFetch(`/facilities/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
      .then((r) => { cacheInvalidate("/facilities"); cacheInvalidate("/deals"); return r; }),
  // Full replace of `pricing_config`: send the stored blob with only the change,
  // or the rest is dropped. The Deal's Payment Report reads it, hence `/deals`.
  updateFacilityPricing: (id: string, body: Record<string, unknown>) =>
    apiFetch(`/facilities/${id}/pricing`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
      .then((r) => {
        cacheInvalidate("/facilities"); cacheInvalidate("/deals");
        return r as { status: string; pricing_config: Record<string, unknown> };
      }),
  // 409 on the last live facility of its Deal (a Deal is never empty); use
  // `deleteDeal`.
  deleteFacility: (id: string, force = false) =>
    apiFetch(`/facilities/${id}${force ? "?force=true" : ""}`, { method: "DELETE" })
      .then((r) => { cacheInvalidate("/facilities"); cacheInvalidate("/deals"); return r; }),
  archiveFacility: (id: string) =>
    apiFetch(`/facilities/${id}`, { method: "DELETE" })
      .then((r) => { cacheInvalidate("/facilities"); cacheInvalidate("/deals"); return r; }),

  // Export
  exportMetricsUrl: (id: string) => `${API_BASE}/facilities/${id}/export/metrics`,
  exportLoanTapeUrl: (id: string, snapshotDate?: string) =>
    `${API_BASE}/facilities/${id}/export/loan-tape${snapshotDate ? `?snapshot_date=${snapshotDate}` : ""}`,

  // AI analysis
  analyze: (facilityId: string, message: string, pipelineResult?: Record<string, unknown>) =>
    apiFetch(`/facilities/${facilityId}/analyze`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message, pipeline_result: pipelineResult }),
    }),

  deleteFile: (facilityId: string, fileId: string) =>
    apiFetch(`/facilities/${facilityId}/files/${fileId}`, { method: "DELETE" }),

  // File download
  downloadFile: async (facilityId: string, fileId: string, filename?: string) => {
    // Try presigned URL first (S3): browser downloads natively without buffering
    const { url: presignedUrl, filename: serverFilename } = await apiFetch(
      `/facilities/${facilityId}/files/${fileId}/presigned-url`
    );
    if (presignedUrl) {
      const a = document.createElement("a");
      a.href = presignedUrl;
      a.download = filename || serverFilename || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
    // Fallback for local storage: proxy through the API
    const res = await fetch(`${API_BASE}/facilities/${facilityId}/files/${fileId}/download`, {
      headers: headers(),
    });
    if (res.status === 401) { clearToken(); window.location.href = "/login"; throw new Error("Session expired"); }
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "download";
    a.click();
    URL.revokeObjectURL(blobUrl);
  },

  // Audit
  getAuditTrail: (id: string) => apiFetch(`/facilities/${id}/audit`),

  // Portfolio
  runPortfolio: (id: string, loanTapePath?: string) => {
    const params = new URLSearchParams();
    if (loanTapePath) params.set("loan_tape_path", loanTapePath);
    return apiFetch(`/facilities/${id}/run-portfolio?${params}`, { method: "POST" });
  },

  // Upload groups (@deprecated — use listUploadGroupsBook)
  listUploadGroups: (id: string) => apiFetch(`/facilities/${id}/upload-groups`),

  // File upload & pipeline — facility-level (@deprecated)
  uploadLoanTape: (id: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/facilities/${id}/upload/loan-tape`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),
  // Bank accounts belong to the contract: the Deal settles through them and its
  // facilities draw from them.
  listBankAccounts: (dealId: string) =>
    apiFetch(`/deals/${dealId}/bank-accounts`) as Promise<{ deal_id: string; total: number; accounts: BankAccountInfo[] }>,
  // The account lives under `/deals` but changes the cockpit's cash header
  // (under `/facilities`), so both prefixes are dropped.
  /** A transit account requires `channel` (422 without it); `fee_pct` (a
   *  fraction) and `fee_fixed` are optional and transit-only. */
  createBankAccount: (
    dealId: string,
    body: {
      account_ref: string; purpose: string; currency?: string; label?: string;
      channel?: BankAccountInfo["channel"]; fee_pct?: number | null; fee_fixed?: number | null;
    },
  ) =>
    apiFetch(`/deals/${dealId}/bank-accounts`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => { cacheInvalidate("/deals"); cacheInvalidate("/facilities"); return r; }),

  // Statement sources: the file formats a Deal receives and where each row goes.
  listStatementSources: (dealId: string) =>
    apiFetch(`/deals/${dealId}/statement-sources`) as Promise<{ deal_id: string; total: number; sources: StatementSource[] }>,
  /** Admin. Parses a file with a saved source, or the one its header matches
   *  when `sourceId` is omitted; persists nothing. */
  previewStatementSource: (dealId: string, file: File, opts?: { sourceId?: string; rows?: number }) =>
    apiDealStatementPost<StatementPreview>(`/deals/${dealId}/statement-sources/preview`, file, {
      source_id: opts?.sourceId,
      rows: opts?.rows,
    }),
  /** Admin. Stores the file's movements in the Deal's accounts and recomputes
   *  the reconciliation of every transit and collections account of the Deal
   *  (`reconciliations`) and its sweeps (`sweeps`), then rebuilds its ledger
   *  (`ledger`). A 422 `unknown_routing_values` stores nothing.
   *  Account movements are read under `/deals` and `/facilities`, so both caches drop. */
  /** `coversFrom` / `coversTo` declare the period the file covers in every
   *  account it reaches; each one left out is the date of its first or last
   *  movement there. 422 (`coverage_invalid`) when it leaves out a movement. */
  uploadDealStatement: (
    dealId: string,
    file: File,
    sourceId?: string,
    coverage?: { coversFrom?: string; coversTo?: string },
  ) =>
    apiDealStatementPost<DealStatementUpload>(`/deals/${dealId}/upload/statement`, file, {
      source_id: sourceId,
      covers_from: coverage?.coversFrom || undefined,
      covers_to: coverage?.coversTo || undefined,
    }).then((r) => { if (r.ok) { cacheInvalidate("/deals"); cacheInvalidate("/facilities"); } return r; }),
  listDealStatements: (dealId: string, opts?: { limit?: number; offset?: number }) =>
    apiFetch(`/deals/${dealId}/statements${qs({ limit: opts?.limit, offset: opts?.offset })}`) as Promise<DealStatementsPage>,
  /** Admin. Corrects the period a statement covers in one account, logged,
   *  and rebuilds the ledger. 422 when it leaves out a movement of the file there. */
  correctDealStatementCoverage: (
    dealId: string, fileId: string, accountId: string, body: DealStatementCoverageRequest,
  ) =>
    apiFetch(`/deals/${dealId}/statements/${fileId}/coverage/${accountId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => { cacheInvalidate("/deals"); return r as DealStatementCoverageCorrection; }),
  /** Admin. Crosses a transit account's credits, or a collections account's
   *  unclassified credits, against the Deal's payments; persists nothing. 422
   *  for an account of another purpose, 404 for one of another Deal. */
  dealReconciliationDryRun: (dealId: string, accountId: string) =>
    apiFetch(`/deals/${dealId}/reconciliation/dry-run${qs({ account_id: accountId })}`, {
      method: "POST",
    }) as Promise<DealDryRun>,
  /** Admin. The dry run, persisted: automatic crosses as AUTO_CONFIRMED and
   *  proposals as PENDING_REVIEW. Running again stores nothing twice. */
  dealReconciliationRun: (dealId: string, accountId: string) =>
    apiFetch(`/deals/${dealId}/reconciliation/run${qs({ account_id: accountId })}`, {
      method: "POST",
    }).then((r) => { cacheInvalidate("/deals"); cacheInvalidate("/facilities"); return r as DealReconciliationRun; }),
  /** Admin. The persisted crosses, newest movement first. */
  listDealMatches: (
    dealId: string,
    opts: { accountId?: string; status?: DealMatchStatus; limit?: number; offset?: number },
  ) =>
    apiFetch(`/deals/${dealId}/reconciliation/matches${qs({
      account_id: opts.accountId,
      status: opts.status,
      limit: opts.limit,
      offset: opts.offset,
    })}`) as Promise<DealMatchesPage>,
  /** Admin. 409 when the cross is not pending or its payment is already in a confirmed one. */
  confirmDealMatch: (dealId: string, matchId: string) =>
    apiFetch(`/deals/${dealId}/reconciliation/matches/${matchId}/confirm`, {
      method: "POST",
    }).then((r) => {
      cacheInvalidate("/deals"); cacheInvalidate("/facilities");
      return r as DealMatchRow & { alias: DealMatchAlias | null };
    }),
  /** Admin. Pending and automatic crosses can be rejected; 409 once resolved. */
  rejectDealMatch: (dealId: string, matchId: string, note?: string) =>
    apiFetch(`/deals/${dealId}/reconciliation/matches/${matchId}/reject`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note: note || null }),
    }).then((r) => { cacheInvalidate("/deals"); cacheInvalidate("/facilities"); return r as DealMatchRow; }),
  /** Admin. 1:1, 1:N or N:1; 422 on N:M, 409 when a movement or payment is
   *  already in a cross an analyst confirmed. */
  createDealMatch: (dealId: string, body: DealManualMatchBody) =>
    apiFetch(`/deals/${dealId}/reconciliation/matches`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => { cacheInvalidate("/deals"); cacheInvalidate("/facilities"); return r as DealManualMatchResult; }),
  /** Admin. Inflows and outflows of a collections account by category, with
   *  the identity. 409 when a movement fits no category; 422 for an account
   *  that is not collections or a range whose start is after its end. */
  getTrustSummary: (dealId: string, accountId: string, range?: { from?: string; to?: string }) =>
    apiFetch(`/deals/${dealId}/reconciliation/trust${qs({
      account_id: accountId,
      from: range?.from,
      to: range?.to,
    })}`) as Promise<TrustSummary>,
  /** Admin. The collections account's movements, newest first, paged on the server. */
  listTrustTransactions: (
    dealId: string,
    accountId: string,
    opts?: { from?: string; to?: string; bucket?: TrustBucket; limit?: number; offset?: number },
  ) =>
    apiFetch(`/deals/${dealId}/reconciliation/trust/transactions${qs({
      account_id: accountId,
      from: opts?.from,
      to: opts?.to,
      bucket: opts?.bucket,
      limit: opts?.limit,
      offset: opts?.offset,
    })}`) as Promise<TrustTransactionsPage>,
  /** Admin. Classifies a trust movement by hand, or removes its category; the
   *  rules never overwrite it afterwards. 422 when the category does not fit
   *  the movement, 409 when an analyst already crossed it with payments. */
  setTrustCategory: (dealId: string, txId: string, body: TrustCategoryRequest) =>
    apiFetch(`/deals/${dealId}/reconciliation/trust/transactions/${txId}/category`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => { cacheInvalidate("/deals"); cacheInvalidate("/facilities"); return r as TrustTransaction; }),
  /** Admin. Applies the Deal's classification rules again to a collections
   *  account, leaving the movements classified by hand. Does not run the cross. */
  classifyTrust: (dealId: string, accountId: string) =>
    apiFetch(`/deals/${dealId}/reconciliation/trust/classify${qs({ account_id: accountId })}`, {
      method: "POST",
    }).then((r) => { cacheInvalidate("/deals"); cacheInvalidate("/facilities"); return r as TrustClassificationRun; }),
  /** Admin. The Deal's gateways at `asOf` (today in the Deal's time zone when
   *  omitted): collected, settled, arrived in the trust and still in the
   *  gateway, with the settlements to look at; and the originator's pending sweep. */
  getDealSweeps: (dealId: string, asOf?: string) =>
    apiFetch(`/deals/${dealId}/reconciliation/sweeps${qs({ as_of: asOf })}`) as Promise<DealSweeps>,
  /** Admin. Recomputes the sweep crosses of every transit account, replacing
   *  the stored ones. The Deal's cash attributes sweeps by them, so both caches drop. */
  runDealSweeps: (dealId: string) =>
    apiFetch(`/deals/${dealId}/reconciliation/sweeps/run`, {
      method: "POST",
    }).then((r) => { cacheInvalidate("/deals"); cacheInvalidate("/facilities"); return r as DealSweepsRun; }),
  /** Admin. The bank ledger as last built, with its unsettled items aged at
   *  `asOf` (today in the Deal's time zone when omitted). */
  getDealLedger: (dealId: string, asOf?: string) =>
    apiFetch(`/deals/${dealId}/reconciliation/ledger${qs({ rail: "bank", as_of: asOf })}`) as Promise<DealLedger>,
  /** Admin. Rebuilds the periods up to the one holding `asOf` and returns the
   *  ledger. 409 when a side does not balance: nothing is stored. */
  buildDealLedger: (dealId: string, asOf?: string) =>
    apiFetch(`/deals/${dealId}/reconciliation/ledger/build${qs({ rail: "bank", as_of: asOf })}`, {
      method: "POST",
    }).then((r) => { cacheInvalidate("/deals"); return r as DealLedger; }),
  getDealLedgerPeriod: (dealId: string, periodId: string, asOf?: string) =>
    apiFetch(`/deals/${dealId}/reconciliation/ledger/periods/${periodId}${qs({ as_of: asOf })}`) as Promise<DealLedgerPeriodDetail>,
  /** Admin. Every balance typed for the Deal's accounts, newest window first. */
  listDealAccountBalances: (dealId: string) =>
    apiFetch(`/deals/${dealId}/reconciliation/balances`) as Promise<{ deal_id: string; balances: DealAccountBalanceEntry[] }>,
  /** Admin. Types or corrects both balances of an account over a period, then
   *  rebuilds the ledger: the balance may complete the period. */
  setDealAccountBalance: (dealId: string, periodId: string, accountId: string, body: DealAccountBalanceRequest) =>
    apiFetch(`/deals/${dealId}/reconciliation/ledger/periods/${periodId}/balances/${accountId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => {
      cacheInvalidate("/deals");
      return r as { balance: DealAccountBalanceEntry; ledger: DealLedgerRefresh };
    }),
  /** Admin. Deletes the balances of an account over a period and rebuilds the ledger. */
  deleteDealAccountBalance: (dealId: string, periodId: string, accountId: string) =>
    apiFetch(`/deals/${dealId}/reconciliation/ledger/periods/${periodId}/balances/${accountId}`, {
      method: "DELETE",
    }).then((r) => { cacheInvalidate("/deals"); return r as { deleted: true; ledger: DealLedgerRefresh }; }),
  listDealLedgerOpenItems: (
    dealId: string,
    opts?: {
      kind?: DealLedgerItemKind; status?: DealLedgerItemState; periodId?: string; asOf?: string;
      limit?: number; offset?: number;
    },
  ) =>
    apiFetch(`/deals/${dealId}/reconciliation/ledger/open-items${qs({
      rail: "bank",
      kind: opts?.kind,
      status: opts?.status,
      period_id: opts?.periodId,
      as_of: opts?.asOf,
      limit: opts?.limit,
      offset: opts?.offset,
    })}`) as Promise<DealLedgerOpenItemsPage>,

  uploadPaymentTape: (id: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/facilities/${id}/upload/payment-tape`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),
  uploadSchedule: (id: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/facilities/${id}/upload/schedule`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),
  uploadCustomer: (id: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/facilities/${id}/upload/customer`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),
  uploadCollateral: (id: string, file: File, snapshotDate?: string, replace?: boolean) =>
    apiUploadAdmin(`/facilities/${id}/upload/collateral`, file, {
      ...(snapshotDate ? { snapshot_date: snapshotDate } : {}),
      ...(replace ? { replace: "true" } : {}),
    }),
  // No facility-scoped run-pipeline: an ingest is book-scoped and cascades to
  // every ACTIVE facility. Use runPipelineBook.

  // Borrower access (allowlist)
  listBorrowerAccess: (borrowerId: string) => apiFetch(`/borrowers/${borrowerId}/access`),
  grantBorrowerAccess: (borrowerId: string, email: string) =>
    apiFetch(`/borrowers/${borrowerId}/access`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    }),
  revokeBorrowerAccess: (borrowerId: string, email: string) =>
    apiFetch(`/borrowers/${borrowerId}/access/${encodeURIComponent(email)}`, { method: "DELETE" }),

  // Granular permissions
  listBorrowerPermissions: (borrowerId: string) => apiFetch(`/borrowers/${borrowerId}/permissions`),
  grantBorrowerPermission: (
    borrowerId: string,
    // `anonymized: true` makes the person anonymous on the whole book (every
    // grant they hold there). Read permissions only: 422 for a write grant, 409
    // when the person already writes on the book.
    body: { email: string; permission: string; deal_id?: string; anonymized?: boolean },
  ) =>
    apiFetch(`/borrowers/${borrowerId}/permissions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  revokeBorrowerPermission: (borrowerId: string, permissionId: string) =>
    apiFetch(`/borrowers/${borrowerId}/permissions/${permissionId}`, { method: "DELETE" }),
  /** A person reads the whole book anonymously or not at all. 409 when switching
   *  it on for someone who uploads to or manages the book. */
  setBorrowerAnonymity: (borrowerId: string, email: string, anonymized: boolean) =>
    apiFetch(`/borrowers/${borrowerId}/anonymity`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, anonymized }),
    }),

  // Borrower self — returns their own permissions
  getMyPermissions: () => apiFetch(`/borrower/me/permissions`),

  // Borrower team management (requires manage_access permission). A scoped
  // grant names a Deal; the scope picker reads `listDeals()`, open to
  // `manage_access` holders.
  getTeamPermissions: () => apiFetch(`/borrower/me/team`),
  grantTeamPermission: (body: { email: string; permission: string; deal_id?: string; anonymized?: boolean }) =>
    apiFetch(`/borrower/me/team`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  revokeTeamPermission: (permissionId: string) =>
    apiFetch(`/borrower/me/team/${permissionId}`, { method: "DELETE" }),
  setTeamAnonymity: (email: string, anonymized: boolean) =>
    apiFetch(`/borrower/me/team/anonymity`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, anonymized }),
    }),

  // Admin
  cancelPipelineRun: (runId: string, reason?: string) =>
    apiFetch(`/admin/pipeline-runs/${runId}/cancel`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason: reason ?? null }),
    }),
  getMaintenanceMode: () => apiFetch(`/admin/maintenance`),
  setMaintenanceMode: (reason?: string) =>
    apiFetch(`/admin/maintenance`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason: reason ?? null }),
    }),
  clearMaintenanceMode: () => apiFetch(`/admin/maintenance`, { method: "DELETE" }),
  listAdminPipelines: () => apiFetch(`/admin/pipelines`),
};

/** `GET /facilities/{id}/terms`: the amendment history, oldest first. Under an
 *  anonymous grant each version keeps its dates and declared fields only. */
export type FacilityTermHistory = {
  as_of: string;
  versions: {
    id: string;
    /** The date the contract says these terms apply from. */
    effective_from: string | null;
    /** Which term blocks the version declares (`current_advance_rate`,
     *  `collateral_terms`, `covenant_config`, `pricing_config`). */
    declared_fields: string[];
  }[];
};
